// =======================================================================================================
// React
import * as React from "react";

// lodash
import _ from "lodash";
import SwipeableViews from 'react-swipeable-views';

// Material UI components
import CircularProgress from 'material-ui/CircularProgress';
import IconMenu from 'material-ui/IconMenu';
import MenuItem from 'material-ui/MenuItem';
import { Tabs, Tab } from 'material-ui/Tabs';
import Snackbar from 'material-ui/Snackbar';
import DropDownMenu from 'material-ui/DropDownMenu';

// Internal components
import { IWidgetThings } from '@UI/Widgets/Things/WidgetsThingsStructs';
import type { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { DashboardConfigurationView } from "./DashboardConfigurationView";
import { LocalizationManagement } from './LocalizationManagement';
import { PagesFolderStructure } from "./PagesFolderStructure";
import { DashboardAlertsInfo } from './DashboardAlerts';

import { buildStructureFromPath, createFlatList } from "@Primitive/TreeStructureHelper";
import { FolderStructure, FolderItemType } from "@Primitive/TreeStructure";
import { showToastNotification } from "@Primitive/ToastNotification";
import { Infotip, InfotipProps } from '@Primitive/Infotip';

import { DebugEx } from "@Utils/DebugEx";

import Documentation from "./YodiboardDocumentation.md";
import { BoardThings, LocationStatus, DrawerPage, PopupPage, ToolbarIcons } from "./BoardThings";
import { PageViewSettings, PageViewWidgetSettingsInfo } from "./PageViewSettings";
import { BoardMacroWidgetsInfo } from "./BoardMacroView";
import { BoardMacroEngine, MacroInfoEx } from "./BoardMacroEngine";
import { BoardMacroDbgView } from "./BoardMacroDbgView";
import { DashboardManagerParent } from "./DashboardManagerParent";

import "./DashboardReporting.less";
import { PageReporting } from "./DashboardReporting";
import { TypoLabel } from "@UI/Primitive/Typography";
import { getZDepthShadow } from "@Utils/Colors";
import { BoardTheming, BoardTheme } from './BoardTheming';
import { YodiBoardExt, WidgetDescriptor, DashboardItemLayoutDesc, DashboardViewWidgets, DashboardType, DashboardConfiguration, PageLayoutPolicy, DashboardStructs } from '@ModelViews/ModelViews';
import Factory from '@UI/Widgets/Factory';
import { getRandomKey, getRandomSmallKey, isBool, isDefined, isFunction, IsNullOrEmpty, isString, isStringEmpty, RunSafe } from '@Utils/Micro';
import { DashboardDescriptor, DashboardDescriptorDictionary, DashboardDescriptorKey, DashboardHelper } from './DashboardStructs';
import { CopyToClipboard, getGuid, inDebug, isEqualsSafe, reduceMapObject, screenHeight, screenWidth } from '@Utils/Utils';
import { WebModule } from '@API/WebModule';
import { WebNode } from '@API/WebNode';
import { Canvas, CanvasButtonEvent, CanvasLayout } from '@UI/Canvas/Canvas';
import { isWebThingKey, WebThingKeys } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { PlegmaStatics } from '@API/PlegmaStatics';
import { CheckSvgUri, YodiwoSVG } from '@UI/Icons/SvgIconsSelector';
import { AdvanceLayout } from '@UI/Layout/AdvanceLayout';
import { CanvasItemDesc, CanvasItemLayoutDesc } from '@UI/Canvas/Structs';
import { IWidget, IWidgetClass, IWidgetDescription, IWidgetDictionary, IWidgetGroupDesc, WidgetThingGenericConfiguration, WidgetType } from '../Widgets/WidgetStructs';
import { IWidgetClassParent } from '@UI/Widgets/IWidgetClassParent';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { ToolbarIconButton, ToolbarIconButtonSeparator, ToolbarIconInfo, ToolbarIconInfo_ButtonRender, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { eIconType, SettingsElementItemsMgmtDialog, SettingsType } from '@Primitive/SettingsListType';
import { Localization, LocalizationLanguageInfo, LocalizationMap } from '@Primitive/Localization/Localization';
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import { IComponentSizeProp, IDebugProp } from '@Primitive/Interfaces';
import { ReactJsonView } from '@Primitive/ReactJsonView';
import { SettingsListItemsDialog } from '@Primitive/SettingsList';
import { TreeStructureLite } from '@Primitive/List/TreeStructureLite';
import { SearchBar } from '@Primitive/SearchBar';
import { Button as RSButton } from 'rsuite';
import { Button, IconButton } from "@Primitive/Button";

import "./DashboardManager.less";
import ListItem from "@Primitive/MUI/ListItem";

const BoardMacroView = React.lazy(() => import("./BoardMacroView"));
const BoardMerging = React.lazy(() => import("./BoardMerging"));
const DebugPortList = React.lazy(() => import('@UI/Widgets/Things/Utils/DebugPortList'));

function print() {
    // add a slight delay to allow the media rules to load
    setTimeout(() => window.print(), 1000);
}
// =======================================================================================================
export interface DashboardTheme {
    Loading: {
        LoadingImageURL: string;
        TextColor: string;
        NoPoweredByText: boolean;
    }
}

export interface DashboardStructure {
    FolderStructure: FolderStructure[];
    // SumFolders:number; // todo: keep # of folders
    SumItems: number;
    FilteredItems: number;
}

const grey600 = "#757575";
const iconSettingsStyle: React.CSSProperties = { color: "#757575", width: 18, height: 18, fontSize: 16 };

const enum PreviewModes {
    Disabled = "Disabled",
    Desktop = "Desktop",
    MobilePhone = "MobilePhone"
}

// =======================================================================================================
export interface DashboardManagerProps extends React.Props<DashboardManager>, IComponentSizeProp, IDebugProp {
    // Define the node key that we need this manager
    nodeKey: string;

    // widget group descriptors
    widgetGroups: IWidgetGroupDesc[];

    // Loaded board
    board: YodiBoardExt;

    // dashboard descriptors
    dashboards: DashboardDescriptorDictionary;

    // Loading Key
    selectedDashboardKey: string;
    onSelectedDashboardKeyChange: (newKey: string) => void;

    // indication that is saving
    isSaving?: boolean;

    // indicate if we failed to save
    isSavingFailed?: boolean;

    // indication of loading
    isLoading?: boolean;

    // Disable editing
    supportEditing: boolean;

    //Events
    onPageSave?: (dashboard: DashboardDescriptor[], updateExternalStorage: boolean) => void;
    onPageAdd?: (dashboard: DashboardDescriptor, updateExternalStorage: boolean) => void;
    onPageDelete?: (key: string, updateExternalStorage: boolean) => void;

    // Theming
    Theme: DashboardTheme;

    DefaultIsListOpen?: boolean;
    DefaultIsListEnabled?: boolean;

    // Callback when default page ID changes.
    onDefaultPageIdChange?: (pageId: string) => void;

    // Callback fired when toolbar icons settings are changed
    onToolbarIconsChange?: (icons: ToolbarIconInfo[]) => void;

    // Save macros
    onMacroUpdate?: (macros: DashboardStructs.MacroInfo[], onMacroSaveSuccess: (status: boolean, msg: string) => void) => void;

    // Toolbar elements
    getToolbarElements?: () => {
        /**
         * Left/Up Elements
         */
        LeftElements?: React.ReactNode[];

        /**
         * Center Elements
         */
        CenterElements?: React.ReactNode[];

        /**
         * Right/Down Elements
         */
        RightElements?: React.ReactNode[];
    }

    // Public domain without user info
    isPublicDomain: boolean;
}
// ----------------------------------------------------------------------------------------------------------
export interface DashboardManagerState {
    // run loading
    isLoading?: boolean;

    // store DashboardDescriptorKey that the user wants to load
    pendingKeyToLoad?: DashboardDescriptorKey;

    /*
     *  Dashboard List
     */

    isListEnabled: boolean;

    // true when list is open
    isListOpen: boolean;
    isListModal: boolean;

    // True when settings is open
    settingsKey?: string;

    /*
     *  Dashboard view
     */
    // descriptor of currently loaded dashboard (if any. Can be null||undefined)
    loadedDashboard: DashboardDescriptor;

    // descriptor of currently dialog loaded dashboard (if any. Can be null||undefined)
    popupPages: {
        dashboard: DashboardDescriptor;
        info: PopupPage;
    }[],

    // true when loaded dashboard is on edit mode and the user
    inEditMode: boolean;
    // true when we are in edit mode and we need to preview the end result
    previewMode: PreviewModes;
    // true when dashboard configuration is open
    isConfigurationOpen: boolean;
    // true when we are showing dbg toolbar
    showDbgToolbar: boolean;

    tooltip: InfotipProps;

    // Custom configuration
    configPageKey: string;
    // true, when loaded dashboard has unsaved changes
    hasUnsavedWork: boolean;
    // true, when we showing hidden pages
    showHiddenPages: boolean;

    // The showing tab of edit mode
    editTabIndex: number;

    // Widget's Clipboard
    WidgetsClipboard: WidgetDescriptor[];

    /*
     * Dialog
     */
    dialogInfo?: DialogConf;

    // Snackbar message
    snackBarMessage: string;
    snackBarOpen: boolean;

    /*
     * Search bar
     */
    isSearchShow: boolean;
    searchKeys: string[];
    emptyValue: boolean;
    defaultSearchValue: string;

    /*
     * Logo Height
     */
    logoHeight: number;

    /*
     * FolderStructure
     */

    // dashboard descriptors
    dashboards: DashboardDescriptorDictionary;

    // object for dashboard's tree structure
    dashboardStructure: DashboardStructure;

    /**
     * Visibility of pages at runtime.
     */
    runtimePagesVisibility: { [key: string]: boolean };

    /**
     * Selected language
     */
    selectedLanguage: string;

    /**
     * Localized labels
     */
    localizationMap: LocalizationMap;

    /**
     * Alert for the specifica board
     */
    alert: DashboardAlertsInfo;

    /**
     * Right drawer info
     */
    loadedRightDrawerPage: DrawerPage;

    /**
     * Bottom drawer info
     */
    loadedBottomDrawerPage: DrawerPage;

    /**
     * Left drawer info
     */
    loadedLeftDrawerPage: DrawerPage;

    /**
     * Whether the user is editing toolbar icons or not.
     */
    editingToolbarIcons: boolean;

    /**
     * Currently selected toolbar icons
     */
    selectedToolbarIcons: string[];

    /**
     * Currently hidden toolbar icons
     */
    hiddenToolbarIcons: string[];

    /**
     * Runtime start/left toolbar icons
     */
    leftToolbarIcons: ToolbarIconInfo[];

    /**
     * Runtime end/right toolbar icons
     */
    rightToolbarIcons: ToolbarIconInfo[];

    /**
     * Runtime toolbar icons
     */
    toolbarIcons: {
        left?: ToolbarIconInfo[];
        center?: ToolbarIconInfo[];
        right?: ToolbarIconInfo[];
    };

    /** 
     * Runtime open/close groups
     */
    widgetsGroups: {
        widgets: boolean;
        hiddenWidgets: boolean;
        clipboard: boolean;
    }
}
// ----------------------------------------------------------------------------------------------------------
export interface IButtonPressedFromAdvancedToolbarIcon {
    element: any;
    data: any;
}
// ----------------------------------------------------------------------------------------------------------
export interface IElementChangedFromAdvancedToolbarIcon {
    jsonName: string;
    elementData: any;
    previousData: any;
    formData: any;
}
// ----------------------------------------------------------------------------------------------------------
const enum eBoardUILabelKey {
    ShowPagesButton = "toolbar-btn-show-pages"
}
// ----------------------------------------------------------------------------------------------------------
export class DashboardManager extends React.Component<DashboardManagerProps, DashboardManagerState> {
    static whyDidYouRender = true;
    private canvasKey: string;
    private _foldersState: { [key: string]: boolean };
    private _localization: Localization;
    private _revision: number;
    private _mounted: boolean;
    // ------------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
        this._mounted = false;
        this.canvasKey = getRandomSmallKey();

        const parentInfo = new IWidgetClassParent(this.CheckForUpdatedData,
            () => this.state.inEditMode,
            this.props.supportEditing,
            () => this.state.showDbgToolbar,
            {
                show: this.showGenericDialog,
                close: this.closeGenericDialog,
                error: (str) => this.setState({ snackBarOpen: true, snackBarMessage: str })
            },
            { showCopy: true });
        let parent = DashboardManagerParent.GetInstance(this.props.nodeKey);
        if (!isDefined(parent))
            parent = new DashboardManagerParent(this.props.nodeKey, parentInfo);
        else
            parent.Info = parentInfo;

        this._foldersState = {};
        this.state = this.getInitialState();
        this._revision = 0;
    }
    // ------------------------------------------------------------------------------------------------------
    // ------------------------------------------------------------------------------------------------------ +++ Localization
    public static LocalizationTextMapKey: string = "Primitive_DashboardManager";
    // ------------------------------------------------------------------------------------------------------
    private _getLocalizationDictionary(dashboards: DashboardDescriptorDictionary): LocalizationMap {
        const res = {
            [eBoardUILabelKey.ShowPagesButton]: 'Show Pages'
        };

        // todo: add for folders translation
        _.forOwn(dashboards, (value, key) => {
            res['page_name_' + value.Key] = value.Settings.Name;

            if (!IsNullOrEmpty(value.Settings.Desc))
                res['page_desc_' + value.Key] = value.Settings.Desc;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * Return label per each button key.
     * Default values are taken from dictionary: `defaultLabelForUILabelKey`
     */
    private _fillLocalizationDictionary(dashboards: DashboardDescriptorDictionary, replaceTexts: boolean): any {
        // get default values
        const defaultValues = this._getLocalizationDictionary(dashboards);

        // try get localization instance
        if (!isDefined(this._localization)) {
            DebugEx.TraceError("DashboardManager: passed `localizationKey`: " + this.props.nodeKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        if (replaceTexts) {
            // update text map
            this._localization.ReplaceTexts(this._localization.DefaultLanguage, DashboardManager.LocalizationTextMapKey, _.cloneDeep(defaultValues));
        }

        // instance found; try get text map
        const map = this._localization.GetTextMap(DashboardManager.LocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceWarning("DashboardManager: passed `localizationKey`: " + this.props.nodeKey + " but not get text map. Initializing text map.");

            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = _.cloneDeep(defaultValues);
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------------ --- Localization
    public getInitialState(): DashboardManagerState {

        this._localization = Localization.GetInstance(this.props.nodeKey);
        let defaultLang = null;

        let localizationMap: LocalizationMap = this._fillLocalizationDictionary(this.props.dashboards, true);
        if (isDefined(this._localization)) {
            defaultLang = this._localization.SelectedLanguage;
        }

        let alert = null;
        try {
            if (isDefined(this.props.board.JsonAlerts))
                alert = JSON.parse(this.props.board.JsonAlerts);
        } catch (ex) {
            DebugEx.TraceError(ex);
        }

        const listEnabled = this.props.supportEditing ? true :
            (isBool(this.props.DefaultIsListEnabled) ? this.props.DefaultIsListEnabled : this.props.board.Settings.DefaultIsListEnabled);
        const state: DashboardManagerState = {
            isLoading: true,
            isListModal: this.props.board.Settings.DefaultIsListFloating,
            isListEnabled: listEnabled,
            isListOpen: listEnabled ?
                (isBool(this.props.DefaultIsListOpen) ? this.props.DefaultIsListOpen : this.props.board.Settings.DefaultIsListOpen) :
                false,
            settingsKey: "",

            loadedDashboard: null,
            popupPages: [],
            inEditMode: false,
            showDbgToolbar: false,
            previewMode: PreviewModes.Disabled,
            hasUnsavedWork: false,
            showHiddenPages: false,
            isConfigurationOpen: false,
            configPageKey: "",
            editTabIndex: 0,
            WidgetsClipboard: [],

            snackBarOpen: false,
            snackBarMessage: "",

            isSearchShow: false,
            searchKeys: [],
            emptyValue: true,
            defaultSearchValue: null,

            dashboardStructure: null,
            dashboards: _.cloneDeep(this.props.dashboards),

            runtimePagesVisibility: {},
            logoHeight: 0,
            tooltip: { open: false, content: '', xPos: 0, yPos: 0 },
            selectedLanguage: defaultLang,
            localizationMap: localizationMap,
            alert: alert,

            loadedRightDrawerPage: null,
            loadedBottomDrawerPage: null,
            loadedLeftDrawerPage: null,

            editingToolbarIcons: false,
            selectedToolbarIcons: [],
            hiddenToolbarIcons: [],

            leftToolbarIcons: [],
            rightToolbarIcons: [],
            toolbarIcons: {},
            widgetsGroups: {
                clipboard: true,
                hiddenWidgets: true,
                widgets: true
            }
        };

        // Init board macro view
        let boardMacroEngine = BoardMacroEngine.GetInstance(this.props.nodeKey);
        if (!isDefined(boardMacroEngine)) {
            boardMacroEngine = new BoardMacroEngine(this.props.nodeKey,
                this.props.board.Id,
                this.props.board.Macros as MacroInfoEx[],
                this.props.supportEditing,
                !isDefined(this.props.board.Settings.WidthListThreshold) ? 800 : this.props.board.Settings.WidthListThreshold);
        } else {
            boardMacroEngine.setInEditMode(this.props.supportEditing);
        }

        // Link Dashboard Things
        let boardThing = BoardThings.GetInstance(this.props.nodeKey);
        if (boardThing != null) {
            boardThing.SetThingsInterface({
                changeSelectedPage: this._changeSelectedPage,
                openDialogDashboard: this.openPopupPage,
                closeDialogDashboard: this.closeDialogDashboard,
                hidePages: this._hidePages,
                unHidePages: this._unhidePages,
                refreshRequest: () => this.forceUpdate(),
                openDrawerPage: this.openDrawerPage,
                closeDrawerPage: this.closeDrawerPage,
                selectToolbarIcons: this.selectToolbarIcons,
                unhideToolbarIcons: this.unhideToolbarIcons,
                hideToolbarIcons: this.hideToolbarIcons,
                addLeftToolbarIcons: this.addLeftToolbarIcons,
                addRightToolbarIcons: this.addRightToolbarIcons,
                setToolbarIcons: this.setToolbarIcons,
                onLanguageChange: this.handleLanguageChange
            });
        } else {
            DebugEx.TraceError("Try to load board that the things are not in place.");
        }

        state.dashboardStructure = this.createDashboardStructure(state);
        return state;
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * For external usage. Reload dashboard pages.
     */
    public reloadDashboard() {
        if (!isStringEmpty(this.props.selectedDashboardKey) && this._mounted) {
            const dashboard = this.state.dashboards[this.props.selectedDashboardKey];
            if (isDefined(dashboard)) {
                this.loadDashboard(dashboard);
            }
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private alertTimer: any;
    private ShowAlert() {
        if (isDefined(this.state.alert)) {
            showToastNotification(
                {
                    Body: this.state.alert.Body,
                    Autoclose: this.state.alert.Autoclose * 1000,
                    Type: "error",
                    Title: this.state.alert.Title,
                    CloseButton: this.state.alert.Autoclose == 0,
                    AdvanceBody: null,
                    HideProgressBar: false,
                    Icon: "fa fa-exclamation-triangle",
                    Position: "top-right"
                },
                null
            );
        }
    }
    // ------------------------------------------------------------------------------------------------------
    get Theme(): BoardTheme {
        let boardTheme = BoardTheming.GetTheme(this.props.nodeKey);
        return boardTheme;
    }
    // ------------------------------------------------------------------------------------------------------
    private async componentDidMountLoading() {
        // Load the token

        // Load the selected dashboard
        await this.loadDashboard(this.state.dashboards[this.props.selectedDashboardKey]);

        // Init periodic notification for alert
        if (isDefined(this.state.alert)) {
            this.ShowAlert();
            this.alertTimer = setInterval(() => this.ShowAlert(), this.state.alert.ReminderPeriod * 1000);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    async componentDidMount() {
        this._mounted = true;
        // Run init macros
        let boardMacroEngine = BoardMacroEngine.GetInstance(this.props.nodeKey);
        if (isDefined(boardMacroEngine)) {
            await this.showGenericDialog({
                body: <div style={{ color: this.props.Theme.Loading.TextColor, padding: 50 }}>
                    <img src={this.props.Theme.Loading.LoadingImageURL} alt="Loading" style={{ display: 'block', margin: 'auto', width: '200px', paddingTop: '20px' }} />
                    <div style={{ padding: '8px 0', margin: 20 }} className="loader">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>Init Board</h3>
                </div>,
                actions: [
                    <IconButton
                        tooltip="Cancel"
                        iconClassName="fas fa-times-circle"
                        iconStyle={{ color: "white" }}
                        onClick={this.closeGenericDialog} />
                ]
            });

            // Load token and wait the first response before init macros
            if (!this.props.isPublicDomain)
                await boardMacroEngine.initTokenSM();

            // Apply less/css macros
            await boardMacroEngine.runLessMacros();

            // Run init macros
            await boardMacroEngine.runInitMacros();
            await this.closeGenericDialog();
        }

        await this.componentDidMountLoading();
        await this.setStateAsync({ isLoading: false });
    }
    // ------------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        // clear any old interval
        if (isDefined(this.alertTimer))
            clearInterval(this.alertTimer);
    }
    // ------------------------------------------------------------------------------------------------------
    private createDashboardMenuList(props: DashboardManagerProps, value: DashboardDescriptor, updateExternalStorage: boolean) {
        const onIconClick = (iconInfo: ToolbarIconInfo) => {
            switch (iconInfo.id) {
                case (`Menu-Structure-Icon-Menu-Settings-Item-${value.Key}`):
                    this.settingsDashboard(value.Key);
                    break;
                case "view-settings":
                    this._openViewSettingsDialog(value.Key);
                    break;
                case (`Menu-Structure-Icon-Menu-Delete-Item-${value.Key}`):
                    this.deleteDashboard(value.Key, updateExternalStorage);
                    break;
                case (`Menu-Structure-Icon-Menu-Clone-Item-${value.Key}`):
                    this.cloneDashboard(value.Key, updateExternalStorage);
                    break;
                case (`Menu-Structure-Icon-Menu-Copy-Item-${value.Key}`):
                    if (CopyToClipboard(value.Settings.PageId)) {
                        this.setState({ snackBarOpen: true, snackBarMessage: "Successfully copy to clipboard." });
                    } else {
                        this.setState({ snackBarOpen: true, snackBarMessage: "Unable copy to clipboard." });
                    }
                    break;
                case ('default-page-menu-item'):
                    // Compute new default page ID.
                    const newPageId: string = !isPageDefault ? value.Key : null;
                    this.props.onDefaultPageIdChange(newPageId);
                    break;
                case ('hide-unhide-page-' + value.Key):
                    this.setState((s: DashboardManagerState) => {
                        const page_: DashboardDescriptor = s.dashboards[value.Key];
                        const isHidden: boolean = !page_.Settings.IsHidden;

                        // If selected dashboard page is hidden unhide it, else hide it.
                        page_.Settings.IsHidden = isHidden;

                        // If this page is loaded update it also.
                        if (s.loadedDashboard != null && s.loadedDashboard.Key == value.Key)
                            s.loadedDashboard.Settings.IsHidden = isHidden;

                        if (_.has(s.runtimePagesVisibility, page_.Key))
                            delete s.runtimePagesVisibility[page_.Key];

                        this.saveDashboard(page_, updateExternalStorage, false);
                        return s;
                    });
                    break;
            }
        }

        if (!props.supportEditing)
            return null;

        if (!_.isFunction(props.onDefaultPageIdChange))
            return null;

        // Check if this page is set as the default one.
        const isPageDefault: boolean = !_.isEmpty(props.board.Settings.DefaultPageId) && _.isEqual(props.board.Settings.DefaultPageId, value.Key);

        let toolbarMenuIcon: ToolbarIconInfo[] = [
            {
                // key:{"Menu-Structure-Icon-Menu-" + value.Key}
                id: "Menu-Structure-Icon-Menu-" + value.Key,
                type: "icon",
                icon: "fas fa-ellipsis-v",
                iconColor: grey600,
                iconType: 0,
                autoSelect: false,
                onIconClickDropdown: true,
                isClickable: false,
                style: {
                    ...iconSettingsStyle,
                    height: "40px",
                    display: "flex",
                    alignItems: "center"
                },
                nestedItems: [
                    {
                        id: "Menu-Structure-Icon-Menu-Settings-Item-" + value.Key,
                        type: "icon",
                        label: "Settings",
                        tooltip: "Settings",
                        icon: "fas fa-cog",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle
                    },
                    {
                        id: "view-settings",
                        type: "icon",
                        label: "View Settings",
                        tooltip: "View Settings",
                        icon: "fas fa-grip-horizontal",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle,
                    },
                    {
                        id: "Menu-Structure-Icon-Menu-Delete-Item-" + value.Key,
                        type: "icon",
                        label: "Delete",
                        tooltip: "Delete",
                        icon: "fas fa-trash",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle
                    },
                    {
                        id: "Menu-Structure-Icon-Menu-Clone-Item-" + value.Key,
                        type: "icon",
                        label: "Clone",
                        tooltip: "Clone",
                        icon: "fas fa-clone",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle
                    },
                    {
                        id: "Menu-Structure-Icon-Menu-Copy-Item-" + value.Key,
                        type: "icon",
                        label: "Copy Page ID",
                        tooltip: "Copy Page ID",
                        icon: "fas fa-key",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle
                    },
                    {
                        id: 'hide-unhide-page-' + value.Key,
                        type: "icon",
                        label: value.Settings.IsHidden ? 'Unhide' : 'Hide',
                        tooltip: value.Settings.IsHidden ? 'Unhide' : 'Hide',
                        icon: value.Settings.IsHidden ? "fas fa-eye" : "fas fa-low-vision",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle
                    },
                    {
                        id: 'default-page-menu-item',
                        type: "icon",
                        label: !isPageDefault ? 'Set as Start Page' : 'Remove as Start Page',
                        tooltip: !isPageDefault ? 'Set as Start Page' : 'Remove as Start Page',
                        icon: !isPageDefault ? "fas fa-star" : "far fa-star",
                        iconType: 0,
                        iconColor: grey600,
                        autoSelect: true,
                        onIconClickDropdown: false,
                        style: iconSettingsStyle
                    }
                ]
            }];

        const iconMenu = ToolbarIconInfo_ButtonRender(toolbarMenuIcon, [], [], {}, onIconClick);
        return <div key={"Menu-Structure-Icon-Menu-" + value.Key}>{iconMenu}</div>;
    }
    // ------------------------------------------------------------------------------------------------------
    private createDashboardStructure(state: DashboardManagerState, props: DashboardManagerProps = this.props, disableHiddenMechanism: boolean = false): DashboardStructure {
        const dashboardStruct: DashboardStructure = { FolderStructure: [], SumItems: 0, FilteredItems: 0 };
        const filteredDashboards: DashboardDescriptorDictionary = state.emptyValue == false ? this.filterDashboardList(state.dashboards, state.searchKeys) : state.dashboards;
        if (_.size(filteredDashboards) > 0) {
            // Sort dashboards by order.
            const sortedDashboards = _.sortBy(filteredDashboards, x => x.Settings.Order);

            let folderStruct: FolderStructure[] = [];
            let parentPathDict = {};
            parentPathDict["/"] = 0;

            // Arrow function to compute if a page is set as default.
            const isPageDefault = (defaultPageId: string, pageId: string) => {
                return _.isFunction(props.onDefaultPageIdChange) &&
                    !_.isEmpty(defaultPageId) &&
                    _.isEqual(defaultPageId, pageId);
            };

            const pageSelectedStyle: React.CSSProperties = {
                color: this.Theme.pageList.pageSelectionColor,
                fontWeight: 'bold',
                backgroundColor: "rgba(0,0,0,0.1)",
                borderLeft: "4px solid " + this.Theme.pageList.pageSelectionColor
            };
            const pageHiddenSettings: React.CSSProperties = {
                background: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(204, 204, 204, 0.5) 10px, rgba(204, 204, 204, 0.5) 20px)  top left fixed"
            };

            var id = 1; // id for the list object
            _.forEach(sortedDashboards, v => {
                const useRuntime: boolean = _.has(state.runtimePagesVisibility, v.Key);
                const isPageHidden: boolean = useRuntime
                    ? !state.runtimePagesVisibility[v.Key]
                    : v.Settings.IsHidden;

                // Is current page selected?
                const isPageSelected = state.loadedDashboard != null &&
                    v.Key == state.loadedDashboard.Key;

                const leftIcon: string[] = [v.Settings.Icon];
                if (props.supportEditing && isPageDefault(props.board.Settings.DefaultPageId, v.Key))
                    leftIcon.push("fa-star text-danger")

                if (_.has(parentPathDict, v.Settings.Path)) {
                    if (!isPageHidden ||
                        state.showHiddenPages ||
                        disableHiddenMechanism) {
                        folderStruct.push({
                            _id: id,
                            children: [],
                            path: v.Settings.Path,
                            type: FolderItemType.Dashboard,
                            key: v.Key,
                            parentId: parentPathDict[v.Settings.Path],
                            primaryText: state.localizationMap['page_name_' + v.Key],
                            secondaryText: state.localizationMap['page_desc_' + v.Key],
                            style: isPageHidden && props.supportEditing
                                ? { ...(isPageSelected ? pageSelectedStyle : {}), ...pageHiddenSettings }
                                : (isPageSelected ? pageSelectedStyle : {}),
                            rightIcon: this.createDashboardMenuList(props, v, true),
                            leftFontIcon: leftIcon
                        });
                    }
                    id += 1;
                } else {
                    var split = v.Settings.Path.split("/");
                    // remove first and last item from list
                    split.pop();
                    split.splice(0, 1);

                    if (split.length > 0) {
                        var path = "/";
                        var parentId = 0;

                        for (var i = 0; i < split.length; i++) {
                            if (_.has(parentPathDict, path + split[i] + "/")) {
                                path = path + split[i] + "/";
                                parentId = parentPathDict[path];
                            } else {
                                const folderName: string = split[i];
                                const folderId: number = id;
                                const folderPath: string = path;

                                if (!_.has(this._foldersState, folderId))
                                    this._foldersState[folderId] = !props.board.Settings.CollapsedFolders;

                                folderStruct.push({
                                    _id: folderId, children: [], path: folderPath, type: FolderItemType.Folder, primaryText: folderName,
                                    leftFontIcon: this._foldersState[folderId] ? ["fa-folder-open"] : ["fa-folder"], parentId: parentId, open: this._foldersState[folderId],
                                });

                                path = path + folderName + "/";
                                parentPathDict[path] = id;
                                parentId = id;
                                id += 1;
                            }
                        }

                        if (!isPageHidden ||
                            state.showHiddenPages ||
                            disableHiddenMechanism) {
                            folderStruct.push({
                                _id: id,
                                children: [],
                                path: v.Settings.Path,
                                type: FolderItemType.Dashboard,
                                key: v.Key,
                                parentId: parentId,
                                primaryText: state.localizationMap['page_name_' + v.Key],
                                secondaryText: state.localizationMap['page_desc_' + v.Key],
                                style: isPageHidden && props.supportEditing
                                    ? { ...(isPageSelected ? pageSelectedStyle : {}), ...pageHiddenSettings }
                                    : (isPageSelected ? pageSelectedStyle : {}),
                                rightIcon: this.createDashboardMenuList(props, v, true),
                                leftFontIcon: leftIcon
                            });
                        }

                        id += 1;
                    }
                }
            });

            dashboardStruct.FolderStructure = buildStructureFromPath(folderStruct);
            dashboardStruct.SumItems = id;
            dashboardStruct.FilteredItems = _.size(filteredDashboards);
            return dashboardStruct;
        }
        return dashboardStruct;
    }
    // ------------------------------------------------------------------------------------------------------
    private _changeSelectedPage = (pageId: string) => {
        let selectedDashboardId: string = this.findPageFromPageId(pageId);

        // if found the selected dashboard, then select it
        if (isDefined(selectedDashboardId)) {
            this.changeSelectedDashboard(selectedDashboardId);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _hideUnhidePages(pages: string[], visible: boolean) {
        this.setState((s: DashboardManagerState) => {
            let haveChanges = false;
            _.forEach(pages, (pageId: string) => {
                let selectedDashboardId: string = this.findPageFromPageId(pageId);

                // if found the selected dashboard, then unhide it
                if (isDefined(selectedDashboardId)) {
                    // Mark this page as visible.
                    s.runtimePagesVisibility[selectedDashboardId] = visible;
                    haveChanges = true;
                }
            });

            if (haveChanges)
                s.dashboardStructure = this.createDashboardStructure(s);
            return s;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private _hidePages = (pages: string[]) => this._hideUnhidePages(pages, false);
    private _unhidePages = (pages: string[]) => this._hideUnhidePages(pages, true);
    // ------------------------------------------------------------------------------------------------------
    private onAddWidget(gindex: number, wdesckey: string) {
        if (this.props.debug && inDebug())
            console.log("onAddWidget gindex:%O, wdesckey:%O", gindex, wdesckey);

        if (isDefined(this.state.loadedDashboard) &&
            isDefined(this.props.widgetGroups) &&
            this.props.widgetGroups.length > gindex) {
            // try to get widget group
            let wgroup = this.props.widgetGroups[gindex];
            if (isDefined(wgroup.Widgets)) {
                this.AddWidget(wgroup.Widgets[wdesckey]);
            }
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private AddWidget(wdesc: WidgetDescriptor) {
        if (isDefined(wdesc)) {

            let webModule = WebNode
                .GetInstanceKey(this.props.nodeKey)
                .GetModule(this.state.loadedDashboard.WebModuleKey);

            // clone description first
            const widgetDesc = _.cloneDeep(wdesc);

            // create widget
            let widget = Factory.CommonCreateWidgetThing(widgetDesc, webModule);

            // create new layout entry
            let layoutItem: DashboardItemLayoutDesc = this._GetNewDashboardItemLayoutDesc(this.state.loadedDashboard.ViewWidgets.Layout, widgetDesc, this.state.loadedDashboard.Settings.NumberOfVerticalGridSpaces);

            // Add the widget to dashboard
            this.setState((prevState: DashboardManagerState) => {
                let uid: string = layoutItem.Key;
                prevState.loadedDashboard.ViewWidgets.WidgetsDesc[uid] = widget.Description;
                prevState.loadedDashboard.Widgets[uid] = widget;

                // update LayoutMap
                prevState.loadedDashboard.ViewWidgets.Layout[uid] = layoutItem;

                // mark dashboard as dirty
                prevState.hasUnsavedWork = true;

                // Set for settings the new widget
                prevState.settingsKey = uid;
                return prevState;
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private onHiddenWidgetToggle(widgetKey: string, hide: boolean) {
        this.setState((s: DashboardManagerState) => {
            let widget = s.loadedDashboard.Widgets[widgetKey];

            // Widget exists?
            if (isDefined(widget)) {
                widget.Description.IsHidden = !widget.Description.IsHidden;

                // For hide, update also mobile visibility.
                if (hide)
                    widget.Description.MobileIsHidden = hide;

                s.hasUnsavedWork = this.checkForUnsavedWork(
                    s.dashboards,
                    s.loadedDashboard
                );
            }
            return s;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private settingsDashboard(key: string) {
        this.setState({ isConfigurationOpen: true, configPageKey: key });
    }
    private settingsDashboardClose = () => this.setState({ isConfigurationOpen: false, configPageKey: null });
    // ------------------------------------------------------------------------------------------------------
    private _openViewSettingsDialog(key: string) {
        this.showGenericDialog({
            contentStaticSize: { w: 0.4, h: 0.8 },
            bodyFunc: (w, h) => {
                // Get the related page.
                const page = this.state.dashboards[key];
                if (!isDefined(page))
                    return;
                const PageInfo = _.map(page.Widgets, (w) => {
                    return {
                        Key: w.Description.Key,
                        Title: w.Description.Title,
                        IsHidden: w.Description.IsHidden,
                        MobileIsHidden: w.Description.MobileIsHidden,
                        MobileOrder: w.Description.MobileOrder
                    } as PageViewWidgetSettingsInfo;
                });

                return <PageViewSettings
                    toolbarHeight={this.Theme.dialog.height}
                    PageInfo={PageInfo}
                    onCloseRequest={this.closeGenericDialog}
                    onSaveRequest={(newInfo) => {
                        if (!isFunction(this.props.onPageSave) ||
                            !this.props.supportEditing)
                            return;

                        if (_.isEqual(PageInfo, newInfo))
                            return;

                        this.setState(
                            (s: DashboardManagerState) => {
                                const page = s.dashboards[key];

                                // Copy the data back to dashboard
                                _.forEach(newInfo, (i) => {
                                    let w = _.find(page.Widgets, (w) => w.Description.Key == i.Key);
                                    if (isDefined(w)) {
                                        w.Description.IsHidden = i.IsHidden;
                                        w.Description.MobileIsHidden = i.MobileIsHidden;
                                        w.Description.MobileOrder = i.MobileOrder;
                                    }
                                });

                                // close dialog
                                s.dialogInfo = null;

                                // Update loaded page
                                if (isDefined(s.loadedDashboard))
                                    s.loadedDashboard = _.cloneDeep(s.dashboards[s.loadedDashboard.Key])
                            },
                            () => {
                                this.props.onPageSave([this.state.dashboards[key]], true)
                            }
                        );
                    }}
                />
            }
        })
    }
    // ------------------------------------------------------------------------------------------------------
    private deleteDashboard(key: string, updateExternalStorage: boolean) {
        if (!isDefined(this.props.onPageDelete) || !this.props.supportEditing)
            return;

        const actions = [
            <Button
                label="No"
                styleLabel={{ color: "rgb(255, 64, 129)" }}
                onClick={this.dialogOnAbort}
                shadowLevel={0}
            />,
            <Button
                label="Yes"
                shadowLevel={0}
                onClick={() => {
                    // Handling the case of the selected dashboard is the one we try to delete
                    const { loadedDashboard } = this.state;
                    if (loadedDashboard != null &&
                        loadedDashboard.Key == key) {
                        this.setState(
                            { loadedDashboard: null },
                            () => {
                                this._updatePulseMechanism(null, loadedDashboard);
                            });
                    }

                    // Remove the webmodule from webnode. This will update the server
                    let webNode = WebNode.GetInstanceKey(this.props.nodeKey);
                    webNode.RemoveModuleByKey(this.state.dashboards[key].WebModuleKey);

                    // Update board things
                    let boardThing = BoardThings.GetInstance(this.props.nodeKey);
                    if (boardThing != null) {
                        let newPages = _.clone(this.state.dashboards);
                        newPages[key].Settings.Deleted = true;
                        boardThing.ReInitPage(newPages, null, false);
                    }

                    // Update the server with removed dashboard
                    webNode.SendOverwriteToServer();

                    // Send the delete event to parent
                    this.props.onPageDelete(key, updateExternalStorage);

                    // Close the dialog
                    this.dialogOnAbort();
                }}
            />
        ];

        this.setState({
            dialogInfo: {
                actions: actions,
                body: <div style={{
                    padding: 24,
                    color: 'initial'
                }}>
                    Are you sure you want to delete page: "{this.state.dashboards[key].Settings.Name}"?
                </div>,
                title: "Delete Page",
                handleClose: this.dialogOnAbort
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private cloneDashboard(dashboardId: string, updateExternalStorage: boolean) {
        if (isDefined(this.props.onPageAdd)) {
            // find the old dashboard
            if (!_.has(this.state.dashboards, dashboardId))
                return;
            let oldDashboard = this.state.dashboards[dashboardId];

            let webNode = WebNode.GetInstanceKey(this.props.nodeKey);

            let newDashboardKey = getGuid();
            let dashboardName = oldDashboard.Settings.Name + " (" + newDashboardKey.substr(0, 4) + ")";
            let webModule: WebModule = new WebModule(webNode,
                "WM_" + newDashboardKey.substr(0, 8),
                dashboardName,
                oldDashboard.Settings.SessionBasedPortData);

            //console.error(oldDashboard.Key, newDashboardKey);

            // Find higher order and put it to the end
            let order = 0;
            _.forOwn(this.state.dashboards, (d, k) => {
                if (d.Settings.Order >= order) {
                    order = d.Settings.Order + 1;
                }
            });

            const dashboardSettings = _.cloneDeep(oldDashboard.Settings);
            dashboardSettings.Name = dashboardName;
            dashboardSettings.Desc = oldDashboard.Settings.Desc;
            dashboardSettings.Order = order;
            dashboardSettings.PageId = newDashboardKey;

            let viewWidgets: DashboardViewWidgets = { Layout: {}, WidgetsDesc: {} };
            let widgets: IWidgetDictionary = {};

            // Create the widgets based on view widgets
            _.forEach(oldDashboard.ViewWidgets.WidgetsDesc, (oldWidgetDesc) => {
                let newWidgetDesc = _.cloneDeep(oldWidgetDesc);
                newWidgetDesc.Key = newWidgetDesc.Key + Math.round(Math.random() * 1000);

                let newWidget = Factory.CreateWidgetThing(newWidgetDesc, newWidgetDesc.Data, webModule);
                // copy `Description`
                newWidget.Description = newWidgetDesc;
                // get `DefaultValues` (if empty)
                if (_.keys(newWidget.Description.DefaultValues).length == 0)
                    newWidget.Description.DefaultValues = newWidget.GetDefaultValues();

                // copy layout with new key
                let newLayout = _.cloneDeep(oldDashboard.ViewWidgets.Layout[oldWidgetDesc.Key]);
                newLayout.Key = newWidgetDesc.Key;

                widgets[newWidgetDesc.Key] = newWidget;
                viewWidgets.WidgetsDesc[newWidgetDesc.Key] = newWidgetDesc;
                viewWidgets.Layout[newLayout.Key] = newLayout;
            });


            let newDashboard: DashboardDescriptor = {
                Key: newDashboardKey,
                Settings: dashboardSettings,
                ViewWidgets: viewWidgets,
                Widgets: widgets,
                WebModuleKey: webModule.ModuleKey,
                JsonViewWidgets: "",
                JsonDashboardSettings: "",
                Timestamp: new Date()
            };

            this.props.onPageAdd(newDashboard, updateExternalStorage);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private addNewPage = () => {
        if (isDefined(this.props.onPageAdd)) {
            let webNode = WebNode.GetInstanceKey(this.props.nodeKey);
            let newDashboardKey = getGuid();
            let sessionBasedPortData = true;
            let dashboardName = "New Page (" + newDashboardKey.substr(0, 4) + ")";
            let webModule: WebModule = new WebModule(webNode,
                "WM_" + newDashboardKey.substr(0, 8),
                dashboardName,
                sessionBasedPortData);

            // Find higher order and put it to the end
            let order = 0;
            _.forOwn(this.state.dashboards, (d, k) => {
                if (d.Settings.Order >= order) {
                    order = d.Settings.Order + 1;
                }
            });

            const dashboardSettings = DashboardHelper.getDefaultConfiguration();
            dashboardSettings.PageId = newDashboardKey;
            dashboardSettings.Name = dashboardName;
            dashboardSettings.Desc = dashboardName;
            dashboardSettings.Order = order;
            dashboardSettings.SessionBasedPortData = sessionBasedPortData;

            let newDashboard: DashboardDescriptor = {
                Key: newDashboardKey,
                Settings: dashboardSettings,
                ViewWidgets: {
                    WidgetsDesc: {},
                    Layout: {},
                },
                Widgets: {},
                JsonViewWidgets: "",
                WebModuleKey: webModule.ModuleKey,
                JsonDashboardSettings: "",
                Timestamp: new Date()
            };

            // Update the things based on the new page
            let boardThing = BoardThings.GetInstance(this.props.nodeKey);
            if (boardThing != null) {
                let newPages = _.clone(this.state.dashboards);
                newPages[newDashboard.Key] = newDashboard;
                boardThing.ReInitPage(newPages, newDashboard);
            }

            this.props.onPageAdd(newDashboard, true);

            this.settingsDashboard(newDashboardKey);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private addNewReport = () => {
        if (isDefined(this.props.onPageAdd)) {
            let webNode = WebNode.GetInstanceKey(this.props.nodeKey);
            let newDashboardKey = getGuid();
            let sessionBasedPortData = true;
            let dashboardName = "New Report (" + newDashboardKey.substr(0, 4) + ")";
            let webModule: WebModule = new WebModule(webNode,
                "WM_" + newDashboardKey.substr(0, 8),
                dashboardName,
                sessionBasedPortData);

            // Find higher order and put it to the end
            let order = 0;
            _.forOwn(this.props.dashboards, (d, k) => {
                if (d.Settings.Order >= order) {
                    order = d.Settings.Order + 1;
                }
            });

            const dashboardSettings = DashboardHelper.getDefaultConfiguration();
            dashboardSettings.PageId = newDashboardKey;
            dashboardSettings.Name = dashboardName;
            dashboardSettings.Desc = dashboardName;
            dashboardSettings.Order = order;
            dashboardSettings.SessionBasedPortData = sessionBasedPortData;
            dashboardSettings.Type = DashboardType.Reporting;

            let newDashboard: DashboardDescriptor = {
                Key: newDashboardKey,
                Settings: dashboardSettings,
                ViewWidgets: {
                    WidgetsDesc: {},
                    Layout: {},
                },
                Widgets: {},
                JsonViewWidgets: "",
                WebModuleKey: webModule.ModuleKey,
                JsonDashboardSettings: "",
                Timestamp: new Date()
            };

            // Update the things based on the new page
            let boardThing = BoardThings.GetInstance(this.props.nodeKey);
            if (boardThing != null) {
                let newPages = _.clone(this.props.dashboards);
                newPages[newDashboard.Key] = newDashboard;
                boardThing.ReInitPage(newPages, newDashboard);
            }

            this.props.onPageAdd(newDashboard, true);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    // saves dashboard,
    private saveDashboard(dashboard: DashboardDescriptor, updateExternalStorage: boolean = true, updateCyan: boolean = true) {
        if (dashboard == null)
            return;

        if (inDebug() && this.props.debug)
            console.log("saveDashboard-----------------------------------");

        // Saved a cloned dashboard !!!
        if (isDefined(this.props.onPageSave))
            this.props.onPageSave([_.cloneDeep(dashboard)], updateExternalStorage);

        let webNode = WebNode.GetInstanceKey(this.props.nodeKey);
        let webModule = webNode.GetModule(dashboard.WebModuleKey);
        if (webModule != null) {

            // Send updates to cyan
            if (updateCyan) {
                // Get all module's ThingKeys.
                let thingKeys: string[] = webModule.GetThingsKeys();

                // Keep only those which don't exist in loaded dashboard.
                _.forEach(dashboard.Widgets, (widget, key) => _.pull(thingKeys, (widget as IWidgetThingsClass<any>).ThingKey));

                // Keep all the things that the Board itself added
                let boardThing = BoardThings.GetInstance(this.props.nodeKey);
                if (boardThing != null) {
                    let pagesThingKeys = boardThing.getPageThingKeys(dashboard.WebModuleKey);
                    _.forEach(pagesThingKeys, (k) => _.pull(thingKeys, k));

                    // Update pages things
                    let newPages = _.clone(this.state.dashboards);
                    newPages[dashboard.Key] = dashboard;
                    boardThing.ReInitPage(newPages, dashboard, false);
                }

                // Remove any remaining things from module.
                webModule.RemoveThings(thingKeys, true);

                // Update all widget things
                _.forOwn(dashboard.Widgets, (w, k) => {
                    if ((w as IWidgetClass<any>).WidgetType == WidgetType.Thing) {
                        (w as IWidgetThingsClass<any>).updateThing();
                    }
                });

                // Force overwrite the modules for this WebNode
                webModule.SendThingSet(true);
            }

            // Force SessionBasedPortData
            webModule.SetSessionBasePorts(dashboard.Settings.SessionBasedPortData);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private discardChanges = () => {
        if (this.state.loadedDashboard != null) {
            // Revert the localization based on older settings
            let oldPage = this.state.dashboards[this.state.loadedDashboard.Key];
            _.forEach(oldPage.Widgets, (w) => w.UpdateLocalization());

            // reload the same dashboard
            this.loadDashboard(_.cloneDeep(oldPage));
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private inMobileMode(): boolean {
        const listWidth = (this.state.isListOpen) ? this.Theme.pageList.width : 0;
        const widthListThreshold = ((this.props.board.Settings.WidthListThreshold != null) ? this.props.board.Settings.WidthListThreshold : 800) - listWidth;
        return this.props.width < widthListThreshold;
    }
    // ------------------------------------------------------------------------------------------------------
    private toggleSearch = () => {
        this.setState((prevState: DashboardManagerState) => {
            prevState.isSearchShow = !prevState.isSearchShow;
            if (!prevState.isSearchShow) {
                prevState.searchKeys = [];
                prevState.emptyValue = true;
                prevState.dashboardStructure = this.createDashboardStructure(prevState);
            }
            return prevState;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private closeList() {
        this.setState({ isListOpen: false });
    }
    // ------------------------------------------------------------------------------------------------------
    private toggleEditMode(state: boolean) {
        this.setState((prevState: DashboardManagerState) => {
            prevState.inEditMode = state;
            prevState.previewMode = PreviewModes.Disabled;
            // when entering in edit mode; show 'Widgets' tab and
            // unselect the latest selected widget
            if (state) {
                prevState.editTabIndex = 0;
                prevState.settingsKey = "";
            }
            return prevState;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private toggleConfVisibility = () => {
        this.setState((prevState: DashboardManagerState) => { prevState.isConfigurationOpen = !prevState.isConfigurationOpen; prevState.configPageKey = ""; return prevState; });
    }
    // ------------------------------------------------------------------------------------------------------
    // Update naming to the module
    private updateDashboardName(newName: string, webModuleKey: string) {
        let module = WebNode.GetInstanceKey(this.props.nodeKey).GetModule(webModuleKey);
        if (module != null)
            module.RenameModule(newName);
    }
    private _layoutCollisionTestArray(l1: DashboardItemLayoutDesc, l2List: DashboardItemLayoutDesc[]): boolean {
        for (var i = 0; i < l2List.length; i++) {
            let l2 = l2List[i];
            if (l1 === l2) continue; // same element
            if (l1.X + l1.W <= l2.X) continue; // l1 is left of l2
            if (l1.X >= l2.X + l2.W) continue; // l1 is right of l2
            if (l1.Y + l1.H <= l2.Y) continue; // l1 is above l2
            if (l1.Y >= l2.Y + l2.H) continue; // l1 is below l2
            return true; // boxes overlap
        }
        return false; // no collision
    }
    // ------------------------------------------------------------------------------------------------------
    private _layoutReorder(layout: { [key: string]: DashboardItemLayoutDesc }, descriptions: { [key: string]: WidgetDescriptor }) {

        // Get layout items sorted from top left to right and down.
        let layoutArray = _.values(layout).sort((a, b) => {
            if (a.Y > b.Y || (a.Y === b.Y && a.X > b.X)) {
                return 1;
            }
            return -1;
        });

        // find the first collision
        let count = layoutArray.length;
        let collisionList = [layoutArray.shift()];
        let collisionId = layoutArray.findIndex(l => l != null && this._layoutCollisionTestArray(l, collisionList));
        while (collisionId >= 0 || count > 0) {
            count--;
            if (collisionId > -1) {
                let collision = layoutArray[collisionId];

                // remove the item from layout Array
                layoutArray[collisionId] = null;

                // find the new Y of collision
                let newY = collision.Y;
                let XStart = collision.X;
                let XEnd = collision.X + collision.W;
                for (var i = 0; i < collisionList.length; i++) {
                    let c = collisionList[i];
                    // The new item must interact with sub-collision item
                    if (!((XEnd <= c.X) ||       // l1 is left of l2
                        (XStart >= c.X + c.W))) { // l1 is right of l2
                        let candY = c.Y + c.H;
                        if (newY < candY)
                            newY = candY; // find the higher Y of the correct X
                    }
                }
                collision.Y = newY;

                // save the new Collision to list and new layout
                collisionList.push(collision);
            } else {
                let item = layoutArray.shift();
                if (item != null)
                    collisionList.push(item);
            }

            // find the next collision
            collisionId = layoutArray.findIndex(l => l != null && this._layoutCollisionTestArray(l, collisionList));
        }

        // Start to move elements to make the layout more compact
        let somethingMoved = true;
        while (somethingMoved) {
            somethingMoved = false;

            _.forOwn(layout, l1 => {
                if (descriptions[l1.Key].DefiesGrid) return; // skip elements that defy grid
                if (l1.Y == 0) return; // the first in stack

                let minY = l1.Y;

                _.forOwn(layout, l2 => {
                    if (l1 === l2) return; // same element
                    if (l1.X + l1.W <= l2.X) return; // l1 is left of l2
                    if (l1.X >= l2.X + l2.W) return; // l1 is right of l2
                    if (l1.Y + l1.H <= l2.Y) return; // l1 is above l2

                    if (l1.Y >= l2.Y + l2.H) {  // l1 is below l2
                        let diff = l1.Y - (l2.Y + l2.H);
                        if (minY > diff)
                            minY = diff;
                    }
                });

                if (minY > 0) {
                    somethingMoved = true;
                    l1.Y = l1.Y - minY;
                }
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private onDashboardConfigurationChanges = (key: string, newConf: DashboardConfiguration) => {
        if (inDebug() && this.props.debug)
            console.log("DashboardManager:onDashboardConfigurationChanges() new Conf%O", newConf);

        this.updateDashboardName(newConf.Name, this.state.dashboards[key].WebModuleKey);

        // todo: update localization
        this._fillLocalizationDictionary(this.state.dashboards, true);

        // update currently loaded dashboard
        if (this.state.inEditMode &&
            this.state.loadedDashboard != null &&
            this.state.loadedDashboard.Key == key) {
            this.setState((s: DashboardManagerState) => {
                let confIsNotEqual = !_.isEqual(s.loadedDashboard.Settings, newConf);
                s.hasUnsavedWork = confIsNotEqual || this.checkForUnsavedWork(s.dashboards, s.loadedDashboard);

                // Check if we need to update the layout of all widgets
                if (s.loadedDashboard.Settings.NumberOfVerticalGridSpaces != newConf.NumberOfVerticalGridSpaces) {
                    let scale = newConf.NumberOfVerticalGridSpaces / s.loadedDashboard.Settings.NumberOfVerticalGridSpaces;
                    _.forOwn(s.loadedDashboard.ViewWidgets.Layout, l => {
                        l.X = Math.min(newConf.NumberOfVerticalGridSpaces, Math.ceil(l.X * scale));
                        l.Y = Math.min(newConf.NumberOfVerticalGridSpaces, Math.ceil(l.Y * scale));
                        l.W = Math.max(1, Math.floor(l.W * scale));
                        l.H = Math.max(1, Math.floor(l.H * scale));
                    });
                    this._layoutReorder(s.loadedDashboard.ViewWidgets.Layout, s.loadedDashboard.ViewWidgets.WidgetsDesc);
                }
                s.loadedDashboard.Settings = newConf;
                this._updatePulseMechanism(s.loadedDashboard, null);
                return s;
            });
        } else {
            this.setState((s: DashboardManagerState) => {
                let dashboard = s.dashboards[key];

                // Check if we need to update the layout of all widgets
                if (dashboard.Settings.NumberOfVerticalGridSpaces != newConf.NumberOfVerticalGridSpaces) {
                    let scale = newConf.NumberOfVerticalGridSpaces / dashboard.Settings.NumberOfVerticalGridSpaces;
                    _.forOwn(dashboard.ViewWidgets.Layout, l => {
                        l.X = Math.min(newConf.NumberOfVerticalGridSpaces, Math.ceil(l.X * scale));
                        l.Y = Math.min(newConf.NumberOfVerticalGridSpaces, Math.ceil(l.Y * scale));
                        l.W = Math.max(1, Math.floor(l.W * scale));
                        l.H = Math.max(1, Math.floor(l.H * scale));
                    });
                    this._layoutReorder(dashboard.ViewWidgets.Layout, dashboard.ViewWidgets.WidgetsDesc);
                }

                dashboard.Settings = newConf;
            }, () => {
                const { loadedDashboard } = this.state;
                let dashboard = this.state.dashboards[key];

                // Save dashboard
                this.saveDashboard(dashboard, true, false);

                // Handling the case that we have change the loaded dashboard
                if (loadedDashboard != null &&
                    loadedDashboard.Key == key) {
                    if (newConf.IsHidden && !this.state.showHiddenPages) {
                        this.setState(
                            { loadedDashboard: null },
                            () => {
                                this._updatePulseMechanism(null, loadedDashboard);
                            });
                    }
                    else {
                        this.loadDashboard(dashboard);
                    }
                }
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private onDashboardLayoutChanges = (newLayout: CanvasLayout) => {
        this.setState((s: DashboardManagerState) => {
            let layoutIsNotEqual = !_.isEqual(s.loadedDashboard.ViewWidgets.Layout, newLayout.LayoutDesc);
            s.loadedDashboard.ViewWidgets.Layout = _.mapValues(newLayout.LayoutDesc, (value) => {
                return {
                    H: value.H,
                    IsDraggable: value.IsDraggable,
                    IsResizable: value.IsResizable,
                    IsStatic: value.IsStatic,
                    Key: value.Key,
                    MaxH: value.MaxH,
                    MaxW: value.MaxW,
                    MinH: value.MinH,
                    MinW: value.MinW,
                    W: value.W,
                    X: value.X,
                    Y: value.Y,
                };
            })
            s.hasUnsavedWork = layoutIsNotEqual || this.checkForUnsavedWork(s.dashboards, s.loadedDashboard);
            return s;
        })
    }
    // ------------------------------------------------------------------------------------------------------
    private onListItemSelected = (key: DashboardDescriptorKey) => {
        if (inDebug() && this.props.debug)
            console.log("DashboardManager:onListItemSelected() key:%O", key);

        if (this.inMobileMode())
            this.closeList();

        // ignore action if the user selected the loaded dashboard
        if (this.state.loadedDashboard != null && this.state.loadedDashboard.Key == key)
            return;

        // check if current loaded graph (if any) is dirty
        if (this.state.loadedDashboard != null && this.state.hasUnsavedWork)
            this.showDialogForUnsavedWork(key);
        else
            this.changeSelectedDashboard(key);
    }
    // ------------------------------------------------------------------------------------------------------
    private onNestedListToggle = (id: number) => {
        this._foldersState[id] = !this._foldersState[id];
        this.setState({ dashboardStructure: this.createDashboardStructure(this.state) });
    };
    // ------------------------------------------------------------------------------------------------------
    private changeSelectedDashboard = (key: string) => {
        if (_.has(this.state.dashboards, key) &&
            ((this.state.loadedDashboard != null && this.state.loadedDashboard.Key != key) || this.state.loadedDashboard == null)) {
            // Call to the parent for updating the selected dashboard
            if (isDefined(this.props.onSelectedDashboardKeyChange))
                this.props.onSelectedDashboardKeyChange(key);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private findPageFromPageId(pageId: string) {
        if (IsNullOrEmpty(pageId))
            return null;

        let selectedDashboardId: string = null;

        // Find page key based on page id
        _.forEach(this.state.dashboards, (page) => {
            if (!_.isEmpty(page) &&
                page.Settings.PageId == pageId) {
                selectedDashboardId = page.Key;
            }
        });

        return selectedDashboardId;
    }
    // ------------------------------------------------------------------------------------------------------
    private openPopupPage = (popupPage: PopupPage) => {
        // Check if we already have open page
        if (_.find(this.state.popupPages, x => x.info.pageId == popupPage.pageId) != null)
            return;

        // try get page from page id
        let selectedDashboardId: string = this.findPageFromPageId(popupPage.pageId);
        if (!isDefined(selectedDashboardId))
            return;

        // default zIndex is zero
        if (!isDefined(popupPage.zIndex))
            popupPage.zIndex = 0;

        // try get dashboard
        const newDashboard = this.state.dashboards[selectedDashboardId];
        if (!isDefined(newDashboard))
            return;

        // try get board thing & stop all pulses
        let boardThing = BoardThings.GetInstance(this.props.nodeKey);
        if (isDefined(boardThing)) {
            boardThing.PulseThingStopAll();
        }

        // enable pulse for dialog
        this._updatePulseMechanism(newDashboard, null);

        // Update settings
        newDashboard.Settings = _.merge({},
            DashboardHelper.getDefaultConfiguration(),
            newDashboard.Settings);

        this.setState((s: DashboardManagerState) => {
            // Try to see if the specific page is replacing any older one
            _.remove(s.popupPages, x => x.info.zIndex == popupPage.zIndex);
            s.popupPages.push({
                dashboard: newDashboard,
                info: popupPage
            });

            s.hasUnsavedWork = false;
            s.inEditMode = false;
            s.isConfigurationOpen = false;
            s.configPageKey = "";
            s.pendingKeyToLoad = undefined;
            return s;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * Used for closing dialog dashboard (from port event & user interaction).
     * Restores pulse mechanism accordingly.
     */
    private closeDialogDashboard = (indexes: number[]) => {
        const { popupPages } = this.state;
        if (!isDefined(popupPages) || popupPages.length == 0)
            return;

        let removedPages = [];

        this.setState((s: DashboardManagerState) => {
            if (indexes == null || indexes.length == 0) {
                // Remove all pages
                removedPages = s.popupPages;
                s.popupPages = [];
            } else {
                // Remove all pages in index
                removedPages = _.remove(s.popupPages, x => _.includes(indexes, x.info.zIndex));
            }

            return s;
        }, () => {
            // try get board thing
            let boardThing = BoardThings.GetInstance(this.props.nodeKey);
            if (!isDefined(boardThing)) {
                console.error("Could not get BoardThings");
                return;
            }

            // stop pulse at dialog dashboard
            _.forEach(removedPages, x => {
                boardThing.PulseThingStop(x.dashboard);
            })


            // restore pulse to loaded dashboard (if any)
            if (isDefined(this.state.loadedDashboard))
                boardThing.PulseThingStart(this.state.loadedDashboard);

            // restore pulse to drawers if any
            const stateKeys = [
                'loadedLeftDrawerPage',
                'loadedRightDrawerPage',
                'loadedBottomDrawerPage'
            ];

            // foreach key
            _.forEach(stateKeys, (key) => {

                // check if property exists
                if (!isDefined(this.state[key]))
                    return;

                // try get dashboard Id from pageId
                let drawerDashboardId: string = this.findPageFromPageId(this.state[key].pageId);
                if (!isDefined(drawerDashboardId))
                    return;

                // try get drawer dashboard
                const drawerDashboard = this.props.dashboards[drawerDashboardId];
                if (isDefined(drawerDashboard))
                    // restart pulse
                    boardThing.PulseThingStart(drawerDashboard);
            });
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private openDrawerPage = (data: DrawerPage, forceOpen: boolean = false) => {

        // try get selected dashboard Id from pageId
        let selectedDashboardId: string = this.findPageFromPageId(data.pageId);
        if (!isDefined(selectedDashboardId))
            return;

        // try get selected dashboard
        const newDashboard = this.state.dashboards[selectedDashboardId];
        if (!isDefined(newDashboard))
            return;

        // from position decide which property name to use
        let stateKey = '';
        switch (data.position) {
            case 'left':
                stateKey = 'loadedLeftDrawerPage';
                // fix `fontIcon`
                if (IsNullOrEmpty(data.fontIcon))
                    data.fontIcon = 'fa fa-chevron-right';
                break;
            case 'bottom':
                stateKey = 'loadedBottomDrawerPage';
                // fix `fontIcon`
                if (IsNullOrEmpty(data.fontIcon))
                    data.fontIcon = 'fa fa-chevron-up';
                break;
            case 'right':
                stateKey = 'loadedRightDrawerPage';
                // fix `fontIcon`
                if (IsNullOrEmpty(data.fontIcon))
                    data.fontIcon = 'fa fa-chevron-left';
                break;
        }

        // Check if we have any changes to do
        if (!forceOpen &&
            _.isEqual(this.state[stateKey], data))
            return;


        this.setState((s: DashboardManagerState) => {

            // initialize runtime variable(s)
            if (data.open == null || forceOpen)
                data.open = true;

            // try get old dashboard (if any)
            let oldDashboard: DashboardDescriptor = null;
            if (isDefined(s[stateKey])) {

                // try get dashboard Id from old pageId
                let oldDashboardId: string = this.findPageFromPageId(s[stateKey].pageId);
                if (isDefined(oldDashboardId))
                    // try get old dashboard
                    oldDashboard = this.state.dashboards[oldDashboardId];
            }

            // update pulse mechanism
            this._updatePulseMechanism(newDashboard, oldDashboard);

            // update state
            s[stateKey] = data;

            s.hasUnsavedWork = false;
            s.inEditMode = false;
            s.isConfigurationOpen = false;
            s.configPageKey = '';
            s.pendingKeyToLoad = undefined;
            return s;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private closeDrawerPage = (position: string, remove: boolean = true) => {
        // keep variable names that need to updated
        const stateKeys = [];

        // check `position`
        if (IsNullOrEmpty(position) ||
            // backwards compatibility to old boards; if true, hide all
            (isString(position) && position.toLowerCase() == 'true')
        ) {
            stateKeys.push(...[
                'loadedLeftDrawerPage',
                'loadedRightDrawerPage',
                'loadedBottomDrawerPage'
            ]);
        }
        else if (position == 'left') {
            stateKeys.push('loadedLeftDrawerPage');
        }
        else if (position == 'right') {
            stateKeys.push('loadedRightDrawerPage');
        }
        else if (position == 'bottom') {
            stateKeys.push('loadedBottomDrawerPage');
        }

        // Check if we have changes
        let isChanged = false;
        _.forEach(stateKeys, (key) => {
            // check if we need to remove or hide the drawer
            if ((this.state[key] != null) &&
                (remove || this.state[key].open)) {
                isChanged = true
            }
        });

        // Run set state only if we have changes 
        if (isChanged)
            this.setState((s: DashboardManagerState) => {
                _.forEach(stateKeys, (key) => {
                    // check if an old dashboard was loaded and update pulse mechanism if needed
                    if (isDefined(s[key])) {

                        // try get dashboard Id from pageId
                        let oldDashboardId: string = this.findPageFromPageId(s[key].pageId);
                        if (isDefined(oldDashboardId)) {

                            // try get dashboard
                            const oldDashboard = s.dashboards[oldDashboardId];
                            if (isDefined(oldDashboard))
                                // stop pulse
                                this._updatePulseMechanism(null, oldDashboard);
                        }
                    }

                    // check if we need to remove or hide the drawer
                    if (remove)
                        s[key] = null;
                    else if (isDefined(s[key])) {
                        s[key].open = false;
                    }
                });

                return s;
            });
    }
    // ------------------------------------------------------------------------------------------------------
    private selectToolbarIcons = (icons: string[]) => {
        this.setState({ selectedToolbarIcons: icons });
    };
    // ------------------------------------------------------------------------------------------------------
    private hideToolbarIcons = (icons: string[]) => {
        this.setState((s: DashboardManagerState) => {
            s.hiddenToolbarIcons = _.uniq(_.concat(s.hiddenToolbarIcons, icons));
            return s;
        });
    };
    // ------------------------------------------------------------------------------------------------------
    private unhideToolbarIcons = (icons: string[]) => {
        this.setState((s: DashboardManagerState) => {
            s.hiddenToolbarIcons = _.pull(s.hiddenToolbarIcons, ...icons);
            return s;
        });
    };
    // ------------------------------------------------------------------------------------------------------
    private addLeftToolbarIcons = (icons: ToolbarIconInfo[]) => this.setState({ leftToolbarIcons: icons })
    // ------------------------------------------------------------------------------------------------------
    private addRightToolbarIcons = (icons: ToolbarIconInfo[]) => this.setState({ rightToolbarIcons: icons })
    // ------------------------------------------------------------------------------------------------------
    private setToolbarIcons = (data: ToolbarIcons) => this.setState({ toolbarIcons: data })
    // ------------------------------------------------------------------------------------------------------
    /**
     * Ping pulse mechanism about new and/or old dashboards.
     * It also fires the Active Dashboard things.
     */
    private _updatePulseMechanism(newDashboard: DashboardDescriptor, oldDashboard: DashboardDescriptor) {
        // try get board thing
        let boardThing = BoardThings.GetInstance(this.props.nodeKey);
        if (!isDefined(boardThing)) {
            console.error("Could not get BoardThings");
            return;
        }

        // stop pulse at old dashboard (if any)
        if (isDefined(oldDashboard))
            boardThing.PulseThingStop(oldDashboard);

        // check if `newDashboard` is specified
        if (!isDefined(newDashboard))
            return;

        // update active dashboard's thing output port
        boardThing.UpdateActiveDashboardPortState(newDashboard.WebModuleKey);

        // Start pulse mechanism
        boardThing.PulseThingStart(newDashboard);
    }
    // ------------------------------------------------------------------------------------------------------
    setStateAsync(state: Partial<DashboardManagerState> | ((s: DashboardManagerState) => DashboardManagerState)) {
        return new Promise((resolve) => {
            if (this._mounted)
                this.setState(state as any, () => resolve(this.state))
            else
                resolve(this.state);
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private async loadDashboard(dashboard: DashboardDescriptor) {
        if (isDefined(dashboard)) {
            // Update the title
            document.title = dashboard.Settings.Name;

            // clone dashboard and fix settings
            const newDashboard = _.cloneDeep(dashboard);
            newDashboard.Settings = _.merge({}, DashboardHelper.getDefaultConfiguration(), newDashboard.Settings);

            // update pulse mechanism
            this._updatePulseMechanism(newDashboard, this.state.loadedDashboard);

            return this.setStateAsync((prevState: DashboardManagerState) => {
                prevState.loadedDashboard = newDashboard;
                prevState.hasUnsavedWork = this.checkForUnsavedWork(prevState.dashboards, prevState.loadedDashboard);
                prevState.inEditMode = false;
                prevState.isConfigurationOpen = false;
                prevState.configPageKey = "";
                prevState.pendingKeyToLoad = undefined;

                // We need to update this to update the selected dashboard
                prevState.dashboardStructure = this.createDashboardStructure(prevState);
                return prevState;
            });
        }
        else {
            // if loaded dashboard is not defined, reset document title
            document.title = "Emerald";
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _handleCanvasButtonTap = (key: string, type: CanvasButtonEvent) => {

        if (CanvasButtonEvent.Settings == type) {
            // Show settings
            this.setState({ settingsKey: key, editTabIndex: 1 });
            // reset revision
            this._revision = 0;
        }

        if (CanvasButtonEvent.Delete == type) {
            // TODO: Correct handling of Delete
            if (_.has(this.state.loadedDashboard.ViewWidgets.Layout, key)) {
                this.setState((prevState: DashboardManagerState) => {
                    // delete the keys of this component from dashboard
                    delete prevState.loadedDashboard.ViewWidgets.Layout[key];
                    delete prevState.loadedDashboard.ViewWidgets.WidgetsDesc[key];
                    delete prevState.loadedDashboard.Widgets[key];
                    prevState.settingsKey = "";
                    prevState.hasUnsavedWork = this.checkForUnsavedWork(prevState.dashboards, prevState.loadedDashboard);
                    return prevState;
                });
            }
        }

        if (CanvasButtonEvent.Hide == type) {
            // hide widget
            this.onHiddenWidgetToggle(key, true);
        }

        if (CanvasButtonEvent.Copy == type) {
            // copy widget descriptor
            this.setState((prevState: DashboardManagerState) => {
                // try to find WidgetDescriptor
                if (prevState.loadedDashboard.Widgets[key]) {
                    // clone Widget descriptor
                    const widgetDesc = _.cloneDeep(prevState.loadedDashboard.Widgets[key].Description);
                    // assign default key from Factory
                    try {
                        // search WidgetClipboard list if widget is already into it
                        let widget: WidgetDescriptor = _.find(prevState.WidgetsClipboard, function (w) { return w.Key == widgetDesc.Key; });
                        if (widget == null)
                            prevState.WidgetsClipboard.push(_.assign(widgetDesc));
                        else {
                            prevState.snackBarOpen = true;
                            prevState.snackBarMessage = widgetDesc.Title + " is already into clipboard list.";
                        }
                    }
                    catch (ex) {
                        DebugEx.TraceException(ex);
                    }
                }
                return prevState;
            });
        }
    }
    // ======================================================================================================
    private checkForUnsavedWork(dashboards: DashboardDescriptorDictionary,
        loadedDashboard: DashboardDescriptor): boolean {

        // If we don;t support editing don't check if we have unsaved work
        if (!this.props.supportEditing)
            return false;

        if (!isDefined(loadedDashboard))
            return false;
        return !isEqualsSafe(dashboards[loadedDashboard.Key],
            loadedDashboard,
            ["ReactParent", "OverrideDescription"],
            true);
    }
    // ======================================================================================================
    // Dialog for unsaved dashboard
    // ======================================================================================================
    private dialogOnAccept = () => {
        // user accepted to save changes before loading
        this.closeGenericDialog();

        // 'save'
        if (this.state.loadedDashboard != null)
            this.saveDashboard(this.state.loadedDashboard);
    }
    // ------------------------------------------------------------------------------------------------------
    private dialogOnRefuse = () => {
        this.setState({ dialogInfo: undefined, hasUnsavedWork: false });

        // just load selected dashboard
        // don't save any changes
        this.changeSelectedDashboard(this.state.pendingKeyToLoad);
    }
    // ------------------------------------------------------------------------------------------------------
    private dialogOnAbort = () => {
        this.setState({ pendingKeyToLoad: undefined, dialogInfo: undefined });
    }
    // ------------------------------------------------------------------------------------------------------
    private showDialogForUnsavedWork(pendingKey: DashboardDescriptorKey) {
        const actions = [
            <Button
                label="Save changes"
                onClick={this.dialogOnAccept}
                shadowLevel={0}
            />,
            <Button
                label="Load without saving changes"
                onClick={this.dialogOnRefuse}
                shadowLevel={0}
            />,
        ];

        this.setState({
            pendingKeyToLoad: pendingKey,
            dialogInfo: {
                actions: actions,
                body: <div style={{ padding: 20 }}>Loaded dashboard has unsaved work. Save changes?</div>,
                title: "",
                handleClose: this.dialogOnAbort
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private showDialogForLocalizationSettings = () => {
        let mappingPagesInfo = {};
        _.forEach(this.state.dashboards, (pages) => {
            mappingPagesInfo[pages.WebModuleKey] = {
                key: pages.WebModuleKey,
                Name: pages.Settings.Name
            };
        });

        this.showGenericDialog({
            contentStaticSize: { w: 0.75, h: 0.95 },
            bodyFunc: (dialogWidth, dialogHeight) => {
                return <LocalizationManagement
                    width={dialogWidth}
                    height={dialogHeight}
                    toolbarHeight={this.Theme.dialog.height}
                    NodeKey={this.props.nodeKey}
                    PagesInfo={mappingPagesInfo}
                    onCloseRequest={this.dialogOnRefuse}
                    onSaveRequest={this.dialogOnAccept}
                />
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private syncNodeThings = () => {
        let webNode = WebNode.GetInstanceKey(this.props.nodeKey);
        if (isDefined(webNode)) {
            webNode.SendOverwriteToServer();
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private openBoardMerge = () => {
        if (!this.props.supportEditing)
            return;

        this.showGenericDialog({
            contentStaticSize: { w: 0.99, h: 0.98 },
            bodyFunc: (dialogWidth, dialogHeight, theme) => {

                return <div style={{ width: dialogWidth, height: dialogHeight, backgroundColor: "#698492" }}>
                    <React.Suspense fallback={<LazyLoadingAnimation />}>
                        <BoardMerging
                            board={this.props.board}
                            width={dialogWidth}
                            height={dialogHeight}
                            onCloseRequest={this.closeGenericDialog}
                        />
                    </React.Suspense>
                </div>;
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private openBoardMacrosView = (preSelectedGUID: string) => {
        if (!this.props.supportEditing)
            return;
        this.showGenericDialog({
            contentStaticSize: { w: 0.99, h: 0.98 },
            bodyFunc: (dialogWidth, dialogHeight, theme) => {
                let node = WebNode.GetInstanceKey(this.props.nodeKey);
                // Pages info
                let pagesInfo = reduceMapObject(this.state.dashboards, d => {
                    let webModule = node.GetModule(d.WebModuleKey);
                    if (!isDefined(webModule))
                        return undefined;
                    let thingsKeys = _.keys(webModule.Things);

                    // Internal widgets
                    let widgetInfo: BoardMacroWidgetsInfo[] = [];
                    _.forEach(d.Widgets, w => {
                        let widget = w as IWidgetThings;
                        let thing = node.GetThing(widget.ThingKey);
                        if (isDefined(thing)) {
                            let desc = Factory.CreateWidgetThingDescDefault(w.Description.Type);
                            // Remove from the list of things keys
                            _.pull(thingsKeys, widget.ThingKey);
                            widgetInfo.push({
                                Widget: widget,
                                Name: w.Description.Title,
                                Type: w.Description.Type,
                                ThingKey: widget.ThingKey,
                                Icon: desc.IconURI,
                                PageConfigurations: d.Settings,
                                Ports: reduceMapObject(thing.Ports, p => {
                                    if (isWebThingKey(p.PortKey) &&
                                        p.PortKey != WebThingKeys.WidgetSettings)
                                        return undefined;
                                    let type = "";
                                    // The type is opposide from the cyan thing
                                    switch (p.PlegmaPort.ioDirection) {
                                        case Plegma.ioPortDirection.Input: type = "Input"; break;
                                        case Plegma.ioPortDirection.InputOutput: type = "InputOutput"; break;
                                        case Plegma.ioPortDirection.Output: type = "Output"; break;
                                    }
                                    return {
                                        Name: p.PlegmaPort.Name,
                                        Type: type,
                                        PortKey: p.PortKey
                                    };
                                })
                            });

                            // Special handling of tabs widgets
                            if (w.Description.Type == "LayoutTabsWidget") {
                                // TODO: get tabs widgets
                                let tWidgets = (w as any).TabsWidgets as { [key: string]: IWidgetThingsClass<any> };
                                _.forEach(tWidgets, tw => {
                                    let widget = tw as IWidgetThings;
                                    let thing = node.GetThing(widget.ThingKey);
                                    if (isDefined(thing)) {
                                        let desc = Factory.CreateWidgetThingDescDefault(tw.Description.Type);
                                        // Remove from the list of things keys
                                        _.pull(thingsKeys, widget.ThingKey);
                                        widgetInfo.push({
                                            Widget: widget,
                                            Name: tw.Description.Title,
                                            Type: "Tab-" + tw.Description.Type,
                                            ThingKey: widget.ThingKey,
                                            Icon: desc.IconURI,
                                            PageConfigurations: d.Settings,
                                            Ports: reduceMapObject(thing.Ports, p => {
                                                if (isWebThingKey(p.PortKey) &&
                                                    p.PortKey != WebThingKeys.WidgetSettings)
                                                    return undefined;
                                                let type = "";
                                                // The type is opposide from the cyan thing
                                                switch (p.PlegmaPort.ioDirection) {
                                                    case Plegma.ioPortDirection.Input: type = "Input"; break;
                                                    case Plegma.ioPortDirection.InputOutput: type = "InputOutput"; break;
                                                    case Plegma.ioPortDirection.Output: type = "Output"; break;
                                                }
                                                return {
                                                    Name: p.PlegmaPort.Name,
                                                    Type: type,
                                                    PortKey: p.PortKey
                                                };
                                            })
                                        });
                                    }
                                });
                            }
                        }
                    });

                    // Add the remain things to the list
                    _.forEach(thingsKeys, k => {
                        let thing = node.GetThing(k);
                        if (isDefined(thing))
                            widgetInfo.push({
                                Widget: null,
                                Name: thing.Name,
                                Type: "UtilThing",
                                ThingKey: k,
                                Icon: thing.PlegmaThing.UIHints.IconURI,
                                PageConfigurations: null,
                                Ports: reduceMapObject(thing.Ports, p => {
                                    if (isWebThingKey(p.PortKey))
                                        return undefined;

                                    let type = "";
                                    // The type is opposide from the cyan thing
                                    switch (p.PlegmaPort.ioDirection) {
                                        case Plegma.ioPortDirection.Input: type = "Input"; break;
                                        case Plegma.ioPortDirection.InputOutput: type = "InputOutput"; break;
                                        case Plegma.ioPortDirection.Output: type = "Output"; break;
                                    }
                                    return {
                                        Name: p.PlegmaPort.Name,
                                        Type: type,
                                        PortKey: p.PortKey
                                    }
                                })
                            })
                    });

                    return {
                        Name: d.Settings.Name,
                        Path: d.Settings.Path,
                        PageId: d.Settings.PageId,
                        ModuleKey: d.WebModuleKey,
                        WidgetsInfo: widgetInfo
                    };
                });

                // Board Things info
                let boardThings = BoardThings.GetInstance(this.props.nodeKey);
                let webModule = node.GetModule(boardThings.WebModuleKey);
                pagesInfo.push({
                    Name: "** Board System",
                    Path: "",
                    PageId: boardThings.WebModuleKey,
                    ModuleKey: boardThings.WebModuleKey,
                    WidgetsInfo: _.map(webModule.Things, t => {
                        let thing = node.GetThing(t.ThingKey);
                        return {
                            Widget: null,
                            Name: thing.Name,
                            Type: "UtilThing",
                            ThingKey: t.ThingKey,
                            Icon: t.PlegmaThing.UIHints.IconURI,
                            PageConfigurations: null,
                            Ports: _.map(_.filter(thing.Ports, p => !isWebThingKey(p.PortKey)), p => {
                                let type = "";
                                // The type is opposite from the cyan thing
                                switch (p.PlegmaPort.ioDirection) {
                                    case Plegma.ioPortDirection.Input: type = "Input"; break;
                                    case Plegma.ioPortDirection.InputOutput: type = "InputOutput"; break;
                                    case Plegma.ioPortDirection.Output: type = "Output"; break;
                                }
                                return {
                                    Name: p.PlegmaPort.Name,
                                    Type: type,
                                    PortKey: p.PortKey
                                }
                            })
                        }
                    })
                });


                return <div style={{ width: dialogWidth, height: dialogHeight, backgroundColor: "#698492" }}>
                    <React.Suspense fallback={<LazyLoadingAnimation />}>
                        <BoardMacroView
                            BoardId={this.props.board.Id}
                            NodeKey={this.props.nodeKey}
                            width={dialogWidth}
                            height={dialogHeight}
                            Macros={this.props.board.Macros}
                            onCloseRequest={this.dialogOnRefuse}
                            PagesInfo={pagesInfo}
                            PreSelectedGUID={preSelectedGUID}
                            onMacrosSave={(newMacros, onMacroSaveSuccess) => {
                                if (isFunction(this.props.onMacroUpdate))
                                    this.props.onMacroUpdate(newMacros, (success, msg) => {
                                        this.setState({
                                            snackBarOpen: true,
                                            snackBarMessage: (success) ? ("Gems saved successfully.") : ("Gems failed to saved :" + msg)
                                        });

                                        let boardMacroEngine = BoardMacroEngine.GetInstance(this.props.nodeKey);
                                        if (isDefined(boardMacroEngine)) {
                                            boardMacroEngine.setMacros(newMacros as MacroInfoEx[]);
                                        }

                                        onMacroSaveSuccess(success, msg);
                                    });
                            }}
                        />
                    </React.Suspense>
                </div>;
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private openToolbarIcons = () => {
        if (!this.props.supportEditing)
            return;
        this.setState({ editingToolbarIcons: true });
    }
    // ------------------------------------------------------------------------------------------------------
    private openBoardSystemLogs = () => {
        if (!this.props.supportEditing)
            return;
        let boardThing = BoardThings.GetInstance(this.props.nodeKey);
        if (boardThing != null) {
            let thing = WebNode.GetInstanceKey(this.props.nodeKey)?.GetThing(boardThing.MgmtThingKey);
            if (thing != null)
                this.showGenericDialog({
                    contentStaticSize: { w: 0.8, h: 0.95 },
                    actions: null,
                    body: <React.Suspense fallback={<LazyLoadingAnimation />}>
                        <DebugPortList widgetThing={thing} />
                    </React.Suspense>,
                    title: "System Port events",
                    handleClose: this.closeGenericDialog
                });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private openBoardInfo = () => {
        if (!this.props.supportEditing)
            return;
        this.showGenericDialog({
            contentStaticSize: { w: 0.7, h: 0.9 },
            title: "Board Information",
            handleClose: this.dialogOnAbort,
            bodyFunc: (w, h) => {
                let statistics = {};
                // Caclulate the number of widgets and the type of them
                _.forEach(this.props.dashboards, d => {
                    _.forEach(d.Widgets, w => {
                        if (_.has(statistics, w.Description.Type))
                            statistics[w.Description.Type]++;
                        else
                            statistics[w.Description.Type] = 1;
                    });
                });

                let score = 0;
                let classScore = [0, 0, 0, 0, 0];
                let numberOfWidgets = 0;
                let weights = Factory.WidgetCostWeights();
                _.forEach(statistics, (s, k) => {
                    if (_.has(weights, k)) {
                        classScore[weights[k]] += s;
                        score += weights[k] * s;
                        numberOfWidgets += s;
                    } else {
                        DebugEx.TraceError("Missing widget: ", k);
                    }
                });


                return <div style={{ backgroundColor: "rgb(234, 234, 233)", width: w, height: h, padding: 5 }}>
                    <div style={{
                        boxShadow: getZDepthShadow(1),
                        margin: 5,
                        padding: 10,
                        borderRadius: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.9)"
                    }}>
                        <TypoLabel>Widgets Statistics:</TypoLabel>
                        <br />
                        <ReactJsonView
                            name={"Statistics"}
                            key={"json-viewer-output"}
                            src={statistics}
                            theme="monokai"
                            iconStyle="triangle"
                            displayDataTypes={false}
                            displayObjectSize={true}
                            collapseStringsAfterLength={20}
                            enableClipboard={true}
                            indentWidth={4}
                            collapsed={1} />
                    </div>
                    <div style={{
                        boxShadow: getZDepthShadow(1),
                        margin: 5,
                        padding: 10,
                        borderRadius: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.9)"
                    }}>
                        <TypoLabel>Score: {score}</TypoLabel>
                        <br />
                        <TypoLabel>#Widgets: {numberOfWidgets}</TypoLabel>
                        {_.map(classScore, (x, i) => <React.Fragment><br /><TypoLabel>{"#Class " + i + ":  " + x}</TypoLabel></React.Fragment>)}
                    </div>

                </div>
            }
        });
    }
    // ======================================================================================================
    private filterDashboardList(dashboards: DashboardDescriptorDictionary, keys: string[]): DashboardDescriptorDictionary {
        let filteredDashboards: DashboardDescriptorDictionary = {};
        _.forEach(keys, (value: string) => {
            filteredDashboards[value] = dashboards[value];
        });
        return filteredDashboards;
    }
    // ------------------------------------------------------------------------------------------------------
    private getWidgetsList(): React.ReactElement<__MaterialUI.List.ListItemProps>[] {
        let widgetList: React.ReactElement<__MaterialUI.List.ListItemProps>[] = [];
        _.forEach(this.props.widgetGroups, (desc: IWidgetGroupDesc, index: number) => {

            const total: number = _.keys(desc.Widgets).length;
            let count: number = 0;

            // created nested items
            let nestedItems: React.ReactElement<__MaterialUI.List.ListItemProps>[] = [];
            _.each(desc.Widgets, (wdesc: IWidgetDescription, wdesckey: string) => {
                count++;
                nestedItems.push(
                    <ListItem
                        key={wdesckey}
                        primaryText={isDefined(wdesc.Title) ? wdesc.Title : wdesc.Type}
                        onClick={() => { this.onAddWidget(index, wdesckey); }}
                        leftIcon={
                            <div style={{ width: 24, height: 24, padding: 0 }}>
                                <img src={wdesc.IconURI} width={24} height={24} />
                            </div>
                        }
                        style={total == count ? {}
                            : { borderBottom: "1px solid rgb(224, 224, 224)" }}
                    />);
            });

            // Create list with widget groups
            widgetList.push(
                <ListItem
                    primaryText={desc.Name}
                    leftIcon={desc.LeftIcon}
                    key={desc.Name}
                    nestedItems={nestedItems}
                    style={index == 0 ? {}
                        : { borderTop: "1px solid rgb(224, 224, 224)" }}
                />)
        });
        return widgetList;
    }
    // ------------------------------------------------------------------------------------------------------
    private getHiddenWidgetList() {
        if (this.state.loadedDashboard == null)
            return null;

        return _.compact(_.map(this.state.loadedDashboard.Widgets, (value, wKey) => {
            // skip visible widgets
            if (!value.Description.IsHidden) return null;

            return <div
                key={wKey}
                id="clipboard-list-item"
                style={{ borderBottom: "1px solid rgb(224, 224, 224)" }}
            >
                <IconButton
                    tooltip="Un-hide widget"
                    iconClassName="fas fa-eye"
                    onClick={() => { this.onHiddenWidgetToggle(value.Description.Key, false); }}
                    iconStyle={{ color: grey600 }}
                />
                <div id="title-container">
                    <div id="primary">
                        {isDefined(value.Description.Title) ? value.Description.Title : value.Description.Key}
                    </div>
                    <div id="secondary">
                        {isDefined(value.Description.Description) ? value.Description.Description : ""}
                    </div>
                </div>
            </div>
        }));
    }
    // ------------------------------------------------------------------------------------------------------
    private getClipboardList() {
        // translate the widgets to canvas items
        return _.map(this.state.WidgetsClipboard, (value, index: number) => {
            return <div
                key={index}
                id="clipboard-list-item"
                style={{ borderBottom: "1px solid rgb(224, 224, 224)" }}
            >
                <IconButton
                    iconClassName="fas fa-clipboard"
                    iconStyle={{ color: grey600 }}
                    onClick={() => {
                        let w = _.cloneDeep(this.state.WidgetsClipboard[index]);
                        w.Key = Factory.CreateWidgetThingDesc(w.Type).Key;
                        this.AddWidget(w);
                    }} />
                <div id="title-container">
                    <div id="primary">
                        {isDefined(value.Title) ? value.Title : value.Key}
                    </div>
                    <div id="secondary">
                        {isDefined(value.Description) ? value.Description : ""}
                    </div>
                </div>
                <IconButton
                    iconClassName="fas fa-trash"
                    iconStyle={{ color: grey600 }}
                    onClick={() => {
                        this.setState((prevState) => {
                            prevState.WidgetsClipboard.splice(index, 1);
                            return prevState;
                        });
                    }}
                />
            </div>
        });
    }
    // ------------------------------------------------------------------------------------------------------
    private showGenericDialog = (dialogInfo: DialogConf) => {
        return this.setStateAsync({ dialogInfo: dialogInfo });
    }
    // ------------------------------------------------------------------------------------------------------
    private closeGenericDialog = () => {
        return this.setStateAsync({ dialogInfo: undefined });
    }
    // ------------------------------------------------------------------------------------------------------
    private renderDashboardList(listWidth: number, dashboardHeight: number) {
        if (this.state.inEditMode && this.props.supportEditing && isDefined(this.state.loadedDashboard)) {
            // Render Widgets and Settings
            const toolbarHeight = this.Theme.boardToolbar.height ?? 0;
            const bodyHeight = dashboardHeight - toolbarHeight;
            const heightStyle = { height: toolbarHeight };
            const tabsStyle = { height: toolbarHeight, backgroundColor: this.Theme.appToolbar.style.backgroundColor, color: this.Theme.appToolbar.style.fontColor };
            return <div
                key={this.props.key + "-dashboard-paper-list"}
                style={{ width: listWidth, height: dashboardHeight, overflowY: "auto", overflowX: "hidden", color: "black", backgroundColor: "white" }}>
                <Tabs
                    key='tabs'
                    onChange={(value) => { this.setState({ editTabIndex: value }) }}
                    value={this.state.editTabIndex}
                    style={tabsStyle}
                    tabItemContainerStyle={tabsStyle}
                >
                    <Tab key="Widgets" buttonStyle={heightStyle} style={{ height: toolbarHeight, color: this.Theme.appToolbar.style.fontColor }} label="Widgets" value={0} />
                    <Tab key="Settings" buttonStyle={heightStyle} style={{ height: toolbarHeight, color: this.Theme.appToolbar.style.fontColor }} label="Settings" value={1} />
                </Tabs>
                <SwipeableViews
                    key='SwipeableViews'
                    index={this.state.editTabIndex}
                    onChangeIndex={(value) => { this.setState({ editTabIndex: value }) }}
                    style={{ width: listWidth, height: bodyHeight, overflow: "hidden" }}
                >
                    {/* Add widget list */}
                    <div
                        id="widgets-sidebar"
                        style={{ overflowX: "hidden", overflowY: "auto", height: bodyHeight }} >
                        <div
                            id="group-title"
                            key='Widgets-selection'
                            onClick={() => this.setState(s => { s.widgetsGroups.widgets = !s.widgetsGroups.widgets; return s; })}
                        >
                            <div>Widgets</div>
                            <div id="icon" className={(this.state.widgetsGroups.widgets) ? 'fas fa-angle-up' : 'fas fa-angle-down'}></div>
                        </div>

                        {(this.state.widgetsGroups.widgets) ? this.getWidgetsList() : null}

                        <div
                            id="group-title"
                            key='Hidden widgets'
                            onClick={() => this.setState(s => { s.widgetsGroups.hiddenWidgets = !s.widgetsGroups.hiddenWidgets; return s; })}
                        >
                            <div>Hidden widgets</div>
                            <div id="icon" className={(this.state.widgetsGroups.hiddenWidgets) ? 'fas fa-angle-up' : 'fas fa-angle-down'}></div>
                        </div>

                        {(this.state.widgetsGroups.hiddenWidgets) ? this.getHiddenWidgetList() : null}

                        <div
                            id="group-title"
                            key='Clipboard'
                            onClick={() => this.setState(s => { s.widgetsGroups.clipboard = !s.widgetsGroups.clipboard; return s; })}
                        >
                            <div id="title">
                                {this.state.WidgetsClipboard.length > 0 ?
                                    <IconButton
                                        iconClassName="fas fa-times"
                                        tooltip="Empty clipboard"
                                        iconStyle={{ color: grey600 }}
                                        onClick={() => this.setState({ WidgetsClipboard: [] })}
                                    /> : null}
                                <div>Clipboard</div>
                            </div>
                            <div id="icon" className={(this.state.widgetsGroups.clipboard) ? 'fas fa-angle-up' : 'fas fa-angle-down'}></div>
                        </div>

                        {(this.state.widgetsGroups.clipboard) ? this.getClipboardList() : null}
                    </div>

                    {/* Widget Settings */}
                    <div key='widget-settings' style={{ overflowX: "hidden", overflowY: "auto", height: bodyHeight }}>
                        {(this.state.settingsKey != "" &&
                            _.has(this.state.loadedDashboard.Widgets, this.state.settingsKey)) ?
                            (this.state.loadedDashboard.Widgets[this.state.settingsKey] as IWidgetThingsClass<any>).RenderBaseSettings(
                                this.state.settingsKey,
                                listWidth,
                                dashboardHeight,
                                {
                                    show: this.showGenericDialog,
                                    close: this.closeGenericDialog,
                                    error: (str) => this.setState({ snackBarOpen: true, snackBarMessage: str })
                                },
                                (s: WidgetThingGenericConfiguration) => {
                                    if (isDefined(this.state.loadedDashboard)) {
                                        let widget = this.state.loadedDashboard.Widgets[this.state.settingsKey] as IWidgetThings;
                                        let thing = WebNode.GetInstanceKey(widget.NodeKey).GetThing(widget.ThingKey);
                                        if (s.Description.Title != thing.Name)
                                            thing.Rename(s.Description.Title);
                                        if (!_.isEqual(s.Description, widget.Description)) {
                                            this.UpdateWidgetDescription(s.Description);
                                        }
                                    }
                                },
                                this._revision
                            )
                            :
                            <div key='none-selected-widget' style={{ textAlign: 'center', marginTop: 10 }}>
                                No selected widget
                            </div>
                        }
                    </div>
                </SwipeableViews>
            </div>
        } else {
            const searchBarHeight = 45;
            const boardToolbarStyle = this.Theme.boardToolbar.style;

            // Render dashboard list
            return <React.Fragment>
                {this.state.isSearchShow
                    ? <div
                        key={this.props.key + "-dashboard-paper-search-bar"}
                        style={{
                            width: listWidth,
                            backgroundColor: boardToolbarStyle.subBackgroundColor,
                            position: "relative",
                            zIndex: 6,
                            boxShadow: getZDepthShadow(2)
                        }}
                    >
                        <SearchBar
                            key={'dashboard-search-bar'}
                            style={{
                                maxWidth: listWidth,
                                height: searchBarHeight,
                                backgroundColor: boardToolbarStyle.subBackgroundColor,
                                color: boardToolbarStyle.fontColor,
                                borderRadius: undefined,
                                zIndex: 6
                            }}
                            activeTextColor={boardToolbarStyle.fontColor}
                            inactiveTextColor={boardToolbarStyle.fontColor}
                            iconColor={boardToolbarStyle.fontColor}
                            source={_.map(this.state.dashboards, (dash, key) => {
                                return { key: dash.Key, value: dash.Settings.Name + " - " + dash.Settings.PageId };
                            })}
                            onSearch={(keys, emptySearchValue, searchValue) => {
                                this.setState((s: DashboardManagerState) => {
                                    s.searchKeys = isDefined(keys) ? keys : [];
                                    s.emptyValue = emptySearchValue;
                                    s.dashboardStructure = this.createDashboardStructure(s);
                                    s.defaultSearchValue = searchValue;
                                    return s;
                                });
                            }}
                            localizationKey={this.props.nodeKey}
                            defaultSearchValue={this.state.defaultSearchValue}
                        />
                    </div>
                    : null}

                <div style={{
                    overflow: "hidden auto",
                    maxHeight: dashboardHeight - ((this.state.isSearchShow) ? searchBarHeight : 0),
                    backgroundColor: this.Theme.pageList.style.backgroundColor,
                    color: this.Theme.pageList.style.fontColor
                }}>
                    {!isStringEmpty(this.props.board.Settings.Logo)
                        ? <div
                            key={this.props.key + "-dashboard-paper-logo-bar"}
                            style={{
                                width: listWidth,
                                position: "relative",
                                zIndex: 5,
                                boxShadow: getZDepthShadow(2)
                            }}
                        >
                            <img
                                src={this.props.board.Settings.Logo}
                                width={listWidth}
                                style={{ padding: 8 }}
                                onLoad={(ev) => {
                                    // Find image element.
                                    const img = ev.target as HTMLElement;

                                    if (this.state.logoHeight != img.clientHeight)
                                        this.setState({ logoHeight: img.clientHeight });
                                }}
                            />
                        </div>
                        : null}

                    {this.state.dashboardStructure.FilteredItems > 0
                        ? <TreeStructureLite
                            key="TreeStructure"
                            FolderStructure={this.state.dashboardStructure.FolderStructure}
                            onListItemSelected={this.onListItemSelected}
                            style={{
                                margin: 0,
                                padding: 0
                            }}
                            onNestedListToggle={this.onNestedListToggle}
                        />
                        : <div
                            key='no-pages-matches'
                            style={{
                                textAlign: 'center',
                                marginTop: 10
                            }}
                        >
                            {this.state.isSearchShow && !this.state.emptyValue
                                ? 'No matching pages found'
                                : 'There are no pages'}
                        </div>}
                </div>
            </React.Fragment>;
        }
    }
    // ------------------------------------------------------------------------------------------------------
    get widgetsVisibility(): { [key: string]: boolean } {
        // Get the visibility of widgets.
        const boardThing = BoardThings.GetInstance(this.props.nodeKey);
        return isDefined(boardThing) ? boardThing.getWidgetsVisibility() : {};
    }
    // ------------------------------------------------------------------------------------------------------
    private renderDrawerPage(drawerPage: DrawerPage, drawerWidth: number, drawerHeight: number) {
        // find dashboard page from page id
        let selectedDashboardId: string = this.findPageFromPageId(drawerPage.pageId);
        let dashboard = this.state.dashboards[selectedDashboardId];

        // check if it is in mobile mode
        let inMobileMode = this.inMobileMode();

        // translate the widgets to canvas items
        let Layout = DashboardManager.getLayout(
            dashboard,
            this.state.inEditMode,
            inMobileMode,
            drawerHeight,
            this.widgetsVisibility);

        if (!isDefined(Layout))
            Layout = {
                Layout: {
                    LayoutDesc: {},
                    ItemDesc: {}
                },
                heightPixels: 0
            };

        // define the WidthListThreshold
        let WidthListThreshold = (this.props.board.Settings.WidthListThreshold != undefined) ? this.props.board.Settings.WidthListThreshold : 800;
        if (dashboard.Settings.WidthListThreshold != null)
            WidthListThreshold = dashboard.Settings.WidthListThreshold;

        // Render drawer main body
        const settings = BoardTheming.getDashboardConfiguration(this.props.nodeKey, dashboard.Settings);
        return <Canvas key={this.canvasKey + "-drawer"} width={drawerWidth} height={drawerHeight}
            WidthListThreshold={WidthListThreshold}
            SizeW={settings.NumberOfVerticalGridSpaces}
            Layout={Layout.Layout}
            CompactLayout={settings.VerticalCompact}
            InEditMode={false}
            Margin={settings.Margin}
            BackgroundImage={settings.BackgroundImage}
            MobileBackgroundImage={settings.MobileBackgroundImage}
            GridLayout={settings.GridLayout}
            CanvasBackgroundColor={settings.CanvasBackgroundColor}
            Padding={settings.Padding}
            ElementSettings={{
                zDepth: settings.ZDepth,
                BackgroundColor: settings.WidgetBackgroundColor,
                NoBackground: settings.WidgetNoBackground,
                BorderRadius: settings.BorderRadius,
                Outline: settings.Outline
            }}
            HeightPixels={Layout.heightPixels}
        />
    }
    // ------------------------------------------------------------------------------------------------------
    openReorderDialog = () => {
        this.showGenericDialog({
            contentStaticSize: { w: 0.5, h: 0.8 },
            bodyFunc: (w, h) => {
                let dashboardStructure = this.createDashboardStructure(this.state, this.props, true);
                return <PagesFolderStructure
                    height={h}
                    width={w}
                    Structure={dashboardStructure.FolderStructure}
                    SumItems={dashboardStructure.SumItems}
                    onCloseRequest={this.closeGenericDialog}
                    UndeletePage={false}
                    ShowFolderOptions={true}
                    onSaveRequest={(structure, sumItems) => {
                        if (!isFunction(this.props.onPageSave) ||
                            !this.props.supportEditing) {
                            this.closeGenericDialog();
                            return;
                        }

                        let originalFlatList = createFlatList(dashboardStructure.FolderStructure);
                        let newFlatList = createFlatList(structure);
                        // acculumulate the diffs
                        let diffs = _.filter(newFlatList, (n) => {
                            let o = _.find(originalFlatList, (o) => n.key == o.key);
                            if (isDefined(o) &&
                                _.isEqual(o, n)) {
                                return false;
                            }
                            return true;
                        });

                        let boardThing = BoardThings.GetInstance(this.props.nodeKey);
                        // Find if anything is new
                        if (diffs.length > 0) {
                            //console.error("We find a different:", diffs);
                            let changesPages = [];

                            _.forEach(diffs, (d) => {

                                let page = this.state.dashboards[d.key];
                                page.Settings.Order = d.order;
                                page.Settings.Path = d.path;

                                // Construct module's new name.
                                let moduleName: string = (page.Settings.Path
                                    + PlegmaStatics.Constants.HierarchySeparator
                                    + page.Settings.Name
                                ).replace('//', '/')

                                // Remove initial slash if exists.
                                if (moduleName.startsWith(PlegmaStatics.Constants.HierarchySeparator))
                                    moduleName = moduleName.substring(1);

                                // Update web module's name which will be used for thing's hierarcy.
                                this.updateDashboardName(moduleName, page.WebModuleKey);

                                changesPages.push(page);

                                // Update pages things
                                if (boardThing != null) {
                                    boardThing.ReInitPage(this.state.dashboards, page, false);
                                }
                            });

                            // Update the server with the new module names
                            WebNode.GetInstanceKey(this.props.nodeKey).SendUpdateToServer();

                            // Send update pages to the server
                            this.props.onPageSave(changesPages, true);
                            this.setState({ dashboardStructure: this.createDashboardStructure(this.state) });
                        }

                        this.closeGenericDialog();
                    }}
                />
            }
        })
    };
    // ------------------------------------------------------------------------------------------------------
    toggleHiddenPages = () => {
        this.setState((s: DashboardManagerState) => {
            s.showHiddenPages = !s.showHiddenPages;
        }, () => {
            this.setState({ dashboardStructure: this.createDashboardStructure(this.state) });
        });
    }
    // ------------------------------------------------------------------------------------------------------
    expandFolders = () => {
        _.forOwn(this._foldersState, (v, k) => { this._foldersState[k] = true; });
        this.setState({ dashboardStructure: this.createDashboardStructure(this.state) });
    };
    // ------------------------------------------------------------------------------------------------------
    collapseFolders = () => {
        _.forOwn(this._foldersState, (v, k) => { this._foldersState[k] = false; });
        this.setState({ dashboardStructure: this.createDashboardStructure(this.state) });
    };
    // ------------------------------------------------------------------------------------------------------
    onListToolbarIconClick = (iconInfo: ToolbarIconInfo) => {
        switch (iconInfo.id) {
            case ('expand-folders'):
                this.expandFolders();
                break;
            case ('collapse-folders'):
                this.collapseFolders();
                break;
            case ('toggle-hidden-pages'):
                this.toggleHiddenPages();
                break;
            case ('reorder-pages'):
                this.openReorderDialog();
                break;
            case ('localization-settings'):
                this.showDialogForLocalizationSettings();
                break
            case ('force-things-sync'):
                this.syncNodeThings();
                break;
            case ('toolbar-buttons'):
                this.openToolbarIcons();
                break;
            case ('board-information'):
                this.openBoardInfo();
                break;
            case ('board-system-logs'):
                this.openBoardSystemLogs();
                break;
            case ('merge-editor'):
                this.openBoardMerge();
                break;
        }
    }
    // ------------------------------------------------------------------------------------------------------
    getListToolbar(): React.ReactNode[] {
        if (this.state.inEditMode && this.props.supportEditing && isDefined(this.state.loadedDashboard)) {
            return null;
        } else {
            const supportEditing = this.props.supportEditing;
            let items: React.ReactNode[] = [];

            if (supportEditing) {
                items.push(<ToolbarIconButton
                    tooltip="Add new page"
                    tooltipPosition='right'
                    id="icons"
                    key="add-new-page"
                    className="fas fa-file-plus"
                    onClick={this.addNewPage} />);

                items.push(<ToolbarIconButton
                    tooltip="Add new report"
                    id="icons"
                    key="add-new-report"
                    className="fas fa-file-alt"
                    onClick={this.addNewReport} />);

                items.push(<ToolbarIconButton
                    tooltip="Gems Editor"
                    id="icons"
                    key="gem-editor"
                    className="fas fa-laptop-code"
                    onClick={() => this.openBoardMacrosView(null)} />);
            }

            items.push(<ToolbarIconButton
                tooltip="Toggle search bar"
                id="icons"
                key="toggle-search"
                className="fas fa-search"
                onClick={this.toggleSearch} />);

            if (supportEditing || (_.keys(this._foldersState).length > 0)) {

                let nestedItems = [];
                if (_.keys(this._foldersState).length > 0) {
                    nestedItems.push(
                        {
                            id: "expand-folders",
                            type: "icon",
                            label: "Expand Folders",
                            tooltip: "Expand Folders",
                            icon: "fas fa-folder-open",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: "collapse-folders",
                            type: "icon",
                            label: "Collapse Folders",
                            tooltip: "Collapse Folders",
                            icon: "fas fa-folder",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        }
                    );
                }

                if (supportEditing) {
                    nestedItems.push(
                        {
                            id: "reorder-pages",
                            type: "icon",
                            label: "Rearrange Pages",
                            tooltip: "Rearrange Pages",
                            icon: "fas fa-folder-tree",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: "toggle-hidden-pages",
                            type: "icon",
                            label: this.state.showHiddenPages
                                ? 'Hide Hidden Pages'
                                : 'Show Hidden Pages',
                            tooltip: this.state.showHiddenPages
                                ? 'Hide Hidden Pages'
                                : 'Show Hidden Pages',
                            icon: this.state.showHiddenPages ? "fas fa-low-vision" : "fas fa-eye",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: "localization-settings",
                            type: "icon",
                            label: 'Localization Settings',
                            tooltip: 'Localization Settings',
                            icon: "fas fa-language",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: 'force-things-sync',
                            type: "icon",
                            label: 'Force Things Sync',
                            tooltip: 'Force Things Sync',
                            icon: "fas fa-sync-alt",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: 'toolbar-buttons',
                            type: "icon",
                            label: 'Toolbar Buttons',
                            tooltip: 'Toolbar Buttons',
                            icon: "fas fa-window-alt",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: 'board-information',
                            type: "icon",
                            label: 'Board Information',
                            tooltip: 'Board Information',
                            icon: "fas fa-info-circle",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: 'board-system-logs',
                            type: "icon",
                            label: 'Board System Logs',
                            tooltip: 'Board System Logs',
                            icon: "fas fa-bug",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        },
                        {
                            id: 'merge-editor',
                            type: "icon",
                            label: 'Merge Editor',
                            tooltip: 'Merge Editor',
                            icon: "fas fa-code-merge",
                            iconType: 0,
                            iconColor: grey600,
                            autoSelect: true,
                            onIconClickDropdown: false,
                            style: iconSettingsStyle
                        }
                    );
                }

                let toolbarMenuIcon: ToolbarIconInfo[] = [{
                    id: "extra-icons",
                    type: "icon",
                    //tooltip: 'More options',
                    icon: "fas fa-ellipsis-v",
                    iconType: 0,
                    autoSelect: false,
                    onIconClickDropdown: true,
                    style: {
                        width: 35,
                        textAlign: 'center'
                    },
                    nestedItems: nestedItems
                }];

                const iconMenu = ToolbarIconInfo_ButtonRender(toolbarMenuIcon, [], [], {}, this.onListToolbarIconClick);
                items.push(iconMenu);
            }

            return items
        }
    }
    toolbarIconsOnClick = (iconInfo) => {
        // send port event
        BoardThings
            .GetInstance(this.props.nodeKey)
            .SendToolbarIconSelectedPortEvent(iconInfo.id);

        // auto select if needed
        if (iconInfo.autoSelect)
            this.setState({ selectedToolbarIcons: [iconInfo.id] });
    };
    // ------------------------------------------------------------------------------------------------------
    buttonPressedFromAdvancedToolbarIcon = (element: any, data: any) => {
        let payload: IButtonPressedFromAdvancedToolbarIcon = {
            element: element,
            data: data
        };

        // emit event for button press
        BoardThings
            .GetInstance(this.props.nodeKey)
            .sendButtonPressedFromAdvancedToolbarIconPortEvent(payload);
    }
    // ------------------------------------------------------------------------------------------------------
    elementChangedFromAdvancedToolbarIcon = (dataPath: string, data: any, prevData: any, formData: any) => {
        let payload: IElementChangedFromAdvancedToolbarIcon = {
            jsonName: dataPath,
            elementData: data,
            previousData: prevData,
            formData: formData
        };

        // emit event for button press
        BoardThings
            .GetInstance(this.props.nodeKey)
            .sendElementChangedFromAdvancedToolbarIconPortEvent(payload);
    }
    // ------------------------------------------------------------------------------------------------------
    localizationBoardLanguageSelected = (language: string) => {
        // emit event for language change
        BoardThings
            .GetInstance(this.props.nodeKey)
            .sendLocalizationBoardLanguageSelectedPortEvent(language);
    }
    // ------------------------------------------------------------------------------------------------------
    handleLanguageChange = (language: string) => this.setState({ selectedLanguage: language })
    // ------------------------------------------------------------------------------------------------------
    getMainToolbarItems(): React.ReactNode[] {
        var elems: React.ReactNode[] = [];
        const { leftToolbarIcons, rightToolbarIcons, hiddenToolbarIcons, selectedToolbarIcons,
            inEditMode, loadedDashboard,
            previewMode, hasUnsavedWork, showDbgToolbar, selectedLanguage, alert
        } = this.state;

        let iconHeight = this.Theme.boardToolbar.height;
        let iconStyle: React.CSSProperties = {
            fontSize: this.Theme.boardToolbar.fontSize
        };

        let toolbarIcons = _.concat(leftToolbarIcons, this.props.board.Settings.ToolbarIcons, rightToolbarIcons);
        elems = ToolbarIconInfo_ButtonRender(toolbarIcons,
            hiddenToolbarIcons,
            selectedToolbarIcons,
            iconStyle,
            this.toolbarIconsOnClick,
            null,
            this.buttonPressedFromAdvancedToolbarIcon,
            this.elementChangedFromAdvancedToolbarIcon
        );

        // Add seperator to split the system icons from user icons
        if (elems.length > 0)
            elems.push(<ToolbarIconButtonSeparator key={'separator-' + elems.length} />);

        if (!inEditMode &&
            loadedDashboard != null &&
            loadedDashboard.Settings.Type == DashboardType.Reporting) {
            elems.push(<ToolbarIconButton
                style={iconStyle}
                key="print-page-edit"
                tooltip="Print"
                id="icons"
                className={"fas fa-print"}
                onClick={print}
            />);
        }

        if (loadedDashboard != null &&
            !this.inMobileMode() &&
            this.props.supportEditing) {

            // In Edit mode with report page type loaded dashboard
            if (loadedDashboard.Settings.Type == DashboardType.Reporting &&
                inEditMode) {

                /*elems.push(<ToolbarIconButton
                    key="add-page-edit"
                    tooltip="Add Report Page"
                    id="icons"
                    className={"fas fa-plus-square"}
                    onClick={this.addNewReportPage}
                />);*/

                elems.push(<ToolbarIconButton
                    style={iconStyle}
                    key="add-proxy-page-edit"
                    tooltip="Add Proxy Page"
                    id="icons"
                    className={"fas fa-file-import"}
                    onClick={this.addNewProxyReportPage}
                />);

                /*elems.push(<ToolbarIconButton
                    style={iconStyle}
                    key="report-page-reorder-edit"
                    tooltip="Reorder Pages"
                    id="icons"
                    className={"fas fa-sort-amount-down"}
                    onClick={this.addNewReportPage}
                />);*/

                elems.push(<ToolbarIconButtonSeparator key={'separator-' + elems.length} />);
            }

            // Show saving circlular progress
            if (this.props.isSavingFailed)
                elems.push(<ToolbarIconButton
                    style={{
                        ...iconStyle,
                        color: '#E57373'
                    }}
                    key="error"
                    tooltip="Save failed, please try again."
                    id="icons"
                    className='fas fa-exclamation-circle'
                    onClick={() => { this.saveDashboard(loadedDashboard); }} />);

            // Show saving circlular progress
            if (this.props.isSaving)
                elems.push(<CircularProgress
                    key="saving-progress"
                    color={"#FAFAFA"}
                    size={25}
                    thickness={4}
                    style={{
                        margin: '0 10px',
                        transform: 'scale(0.75)'
                    }} />);

            //  Show save button if the layout has unsaved work
            // and if we don't have but we are in edit mode show button for exit edit mode
            if (inEditMode && hasUnsavedWork) {
                elems.push(<ToolbarIconButton
                    style={iconStyle}
                    key="save"
                    tooltip="Save changes"
                    id="icons"
                    className={"fas fa-upload"}
                    onClick={() => { this.saveDashboard(loadedDashboard); }}
                />);
                elems.push(<ToolbarIconButton
                    style={iconStyle}
                    key="discard"
                    tooltip="Discard changes"
                    id="icons"
                    className={"fas fa-times"}
                    onClick={this.discardChanges}
                />);
            } else if (inEditMode) {
                elems.push(<ToolbarIconButton
                    style={iconStyle}
                    key="exit-edit"
                    tooltip="Exit edit mode"
                    id="icons"
                    className={"fas fa-check"}
                    onClick={() => this.toggleEditMode(false)}
                />);
            }

            // on edit mode
            if (inEditMode) {
                let toolbarMenuIcon: ToolbarIconInfo[] = [{
                    id: "preview-settings",
                    type: "icon",
                    tooltip: "Preview",
                    iconColor: 'white',
                    icon: "fas fa-eye",
                    iconType: 0,
                    autoSelect: false,
                    onIconClickDropdown: true,
                    isClickable: false,
                    style: {
                        color: undefined,
                        cursor: undefined,
                        //boxShadow: undefined
                    },
                    nestedItems: [{
                        id: "disabled",
                        type: "icon",
                        label: "Disabled",
                        iconColor: grey600,
                        icon: previewMode === PreviewModes.Disabled ? "fas fa-check" : undefined,
                        iconType: 0,
                        autoSelect: false,
                        isClickable: true,
                        style: iconSettingsStyle
                    },
                    {
                        id: "desktop",
                        type: "icon",
                        label: "Desktop",
                        iconColor: grey600,
                        icon: previewMode === PreviewModes.Desktop ? "fas fa-check" : undefined,
                        iconType: 0,
                        autoSelect: false,
                        isClickable: true,
                        style: iconSettingsStyle
                    },
                    {
                        id: "mobile",
                        type: "icon",
                        label: "Mobile",
                        iconColor: grey600,
                        icon: previewMode === PreviewModes.MobilePhone ? "fas fa-check" : undefined,
                        iconType: 0,
                        autoSelect: false,
                        isClickable: true,
                        style: iconSettingsStyle
                    }]
                }];

                const onIconClick = (iconInfo: ToolbarIconInfo) => {
                    switch (iconInfo.id) {
                        case ('disabled'):
                            this.setState({ previewMode: PreviewModes.Disabled });
                            break;
                        case ('desktop'):
                            this.setState({ previewMode: PreviewModes.Desktop });
                            break;
                        case ('mobile'):
                            this.setState({ previewMode: PreviewModes.MobilePhone });
                            break;
                    }
                }

                const iconMenu = ToolbarIconInfo_ButtonRender(toolbarMenuIcon, [], [], {}, onIconClick);
                elems.push(iconMenu);

                // append button for dashboard settings
                elems.push(<ToolbarIconButton
                    style={iconStyle}
                    key="dashboard-settings"
                    tooltip="Page Settings"
                    id="icons"
                    className={"fas fa-cog"}
                    onClick={this.toggleConfVisibility}
                />);

            } else {
                // on NO-edit mode,

                // append button for port dbg messages edit
                elems.push(
                    <ToolbarIconButton
                        key="dbg"
                        tooltip={showDbgToolbar ? 'Hide debug icon' : 'Show debug icon'}
                        id="icons"
                        style={{
                            color: !showDbgToolbar ? '' : grey600,
                            ...iconStyle
                        }}
                        className={"fas fa-bug"}
                        onClick={() => this.setState({ showDbgToolbar: !showDbgToolbar })}
                    />);

                // append button for macro debug edit
                elems.push(
                    <ToolbarIconButton
                        style={iconStyle}
                        key="dbgMacro"
                        tooltip={'Show gem debug info'}
                        id="icons"
                        className={"fas fa-gem"}
                        onClick={() => this.showGenericDialog({
                            title: "Gems log",
                            contentStaticSize: { w: 0.75, h: 0.95 },
                            bodyFunc: (dialogWidth, dialogHeight, theme) => {
                                return <BoardMacroDbgView
                                    NodeKey={this.props.nodeKey}
                                    height={dialogHeight}
                                    width={dialogWidth}
                                    onOpenEditor={this.openBoardMacrosView}
                                />
                            },
                            handleClose: this.dialogOnAbort
                        })}
                    />);

                // append button for layout edit
                elems.push(
                    <ToolbarIconButton
                        style={iconStyle}
                        key="edit"
                        tooltip="Edit layout"
                        id="icons"
                        className={"fas fa-pen"}
                        onClick={() => this.toggleEditMode(true)}
                    />);
            }
        }

        if (!this.props.board.Settings.HideLocalizationDropdownList) {
            // Language support
            let localization = Localization.GetInstance(this.props.nodeKey);
            if (localization != null) {
                let lang = _.pickBy(localization.GetSupportedLanguages(), (v, k) => v.IsEnabled);
                if (_.keys(lang).length > 1) {
                    let selectedLang = _.find(lang, (l: LocalizationLanguageInfo) => l.Code == selectedLanguage);
                    if (selectedLang && CheckSvgUri(selectedLang.FlagIcon)) {
                        elems.push(
                            <IconMenu
                                key='selected-language'
                                iconButtonElement={<div><YodiwoSVG uri={selectedLang.FlagIcon} height={25} width={25} /></div>}
                                style={{
                                    color: "white",
                                    cursor: "pointer",
                                    paddingLeft: 4,
                                    paddingRight: 4,
                                    paddingBottom: 4
                                }}
                                value={selectedLanguage}>
                                {_.map(lang, (l) => <MenuItem
                                    key={l.Code}
                                    value={l.Code}
                                    primaryText={l.Label}
                                    leftIcon={<div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignContent: 'center',
                                        alignItems: 'center'
                                    }}><YodiwoSVG uri={l.FlagIcon} width={35} height={35} /></div>}
                                    onClick={() => {
                                        // update state, send event and save to storage
                                        this.setState({ selectedLanguage: l.Code });
                                        this.localizationBoardLanguageSelected(l.Code);
                                        localization.ChangeSelectedLanguage({
                                            Language: l.Code,
                                            ReloadBoard: !this.props.board.Settings.HandleLocalizationFromGems
                                        });
                                    }} />)}
                            </IconMenu>
                        );
                    } else {
                        elems.push(
                            <DropDownMenu
                                key='change-selected-language'
                                underlineStyle={{
                                    width: '100%',
                                    margin: 0
                                }}
                                style={{
                                    color: "white",
                                    height: iconHeight,
                                    marginRight: 0,
                                    marginLeft: 4
                                }}
                                labelStyle={{
                                    color: "white",
                                    height: iconHeight,
                                    lineHeight: iconHeight + "px",
                                    paddingRight: 20,
                                    paddingLeft: 0
                                }}
                                iconStyle={{
                                    right: -12,
                                    top: -4
                                }}
                                value={selectedLanguage}
                                onChange={(event, index, value) => {
                                    // update state, send event and save to storage
                                    this.setState({ selectedLanguage: value });
                                    this.localizationBoardLanguageSelected(value);
                                    localization.ChangeSelectedLanguage({
                                        Language: value,
                                        ReloadBoard: !this.props.board.Settings.HandleLocalizationFromGems
                                    });
                                }}>
                                {_.map(lang, (l) => <MenuItem
                                    key={l.Code}
                                    value={l.Code}
                                    primaryText={l.Label}
                                    leftIcon={<div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignContent: 'center',
                                        alignItems: 'center'
                                    }}><YodiwoSVG uri={l.FlagIcon} width={35} height={35} /></div>}
                                />)}
                            </DropDownMenu>
                        );
                    }
                }
            }
        }

        // append button for layout edit
        if (isDefined(alert))
            elems.push(<ToolbarIconButton
                style={{
                    ...iconStyle,
                    color: '#FF5722',
                    cursor: 'initial'
                }}
                key="alert"
                tooltip={alert.Tooltip}
                id="icons"
                className='fas fa-exclamation-triangle' />);

        let boardThing = BoardThings.GetInstance(this.props.nodeKey);
        if (boardThing != null && boardThing.LocationPosition != null && boardThing.LocationPosition.status != LocationStatus.Disabled) {
            let iconClassName = "";
            let iconColor = "";
            switch (boardThing.LocationPosition.status) {
                case LocationStatus.Started:
                case LocationStatus.Stopped:
                    iconClassName = "fas fa-location-slash";
                    iconColor = "white";
                    break;
                case LocationStatus.Error:
                    iconClassName = "fas fa-location-slash";
                    iconColor = "red";
                    break;
                case LocationStatus.Valid:
                    if (boardThing.LocationPosition.acc < 50) {
                        iconClassName = "fas fa-location";
                        iconColor = "white";
                    }
                    else {
                        iconClassName = "fas fa-location";
                        iconColor = "yellow";
                    }
                    break;
                default:
                    break;
            }
            elems.push(<div
                key="gps-status"
                className={iconClassName}
                style={{ ...iconStyle, color: iconColor, padding: 10 }}
            />);
        }

        // check for right drawer
        const { loadedRightDrawerPage } = this.state;
        if (isDefined(loadedRightDrawerPage) &&
            loadedRightDrawerPage.isStatic === false &&
            loadedRightDrawerPage.position == "right" &&
            !loadedRightDrawerPage.open)
            elems.push(<i key='right-drawer-icon' id="icons" className={loadedRightDrawerPage.fontIcon} onClick={() => { this.openDrawerPage(loadedRightDrawerPage, true); }} />);

        return elems;
    }
    // ------------------------------------------------------------------------------------------------------
    renderCanvasPage(dashboardWidth: number, dashboardHeight: number) {
        const dashboard = this.state.loadedDashboard;
        const listWidth = (this.state.isListOpen) ? this.Theme.pageList.width : 0;
        let widthListThreshold = ((this.props.board.Settings.WidthListThreshold != null) ? this.props.board.Settings.WidthListThreshold : 800) - listWidth;

        // use page settings if is exit for width list threashbold
        if (dashboard.Settings.WidthListThreshold != null)
            widthListThreshold = dashboard.Settings.WidthListThreshold;

        // Check if dashboard property is defined
        const inEditMode = this.state.inEditMode && (this.state.previewMode == PreviewModes.Disabled);

        // Enable mobile preview
        if (this.state.previewMode == PreviewModes.MobilePhone)
            dashboardWidth = widthListThreshold;

        const inMobileMode = this.inMobileMode() || (this.state.previewMode == PreviewModes.MobilePhone);

        // translate the widgets to canvas items
        const Layout = DashboardManager.getLayout(
            dashboard,
            inEditMode,
            inMobileMode,
            dashboardHeight,
            this.widgetsVisibility);

        // Find if we have items in dashboard
        if (Layout != null) {
            // Set as a selected item the settings key
            let selectedItems: string[] = inEditMode && this.state.settingsKey != "" ? [this.state.settingsKey] : [];

            const settings = BoardTheming.getDashboardConfiguration(this.props.nodeKey, dashboard.Settings);
            let maxDashboardWidth = dashboardWidth;
            if (maxDashboardWidth > settings.MaxPageWidth)
                maxDashboardWidth = settings.MaxPageWidth;

            return (
                <div id="section-to-print" style={(this.state.inEditMode) ? { backgroundColor: "gray", width: dashboardWidth } : { width: dashboardWidth }}>
                    <div
                        id={settings.PageId}
                        style={{
                            maxWidth: settings.MaxPageWidth,
                            width: maxDashboardWidth,
                            margin: "auto"
                        }}>
                        <Canvas
                            key={this.canvasKey}
                            width={maxDashboardWidth}
                            height={dashboardHeight}
                            WidthListThreshold={widthListThreshold}
                            SizeW={settings.NumberOfVerticalGridSpaces}
                            Layout={Layout.Layout}
                            CompactLayout={settings.VerticalCompact}
                            SelectedItems={selectedItems}
                            InEditMode={inEditMode}
                            Margin={settings.Margin}
                            onCanvasLayoutChanged={this.onDashboardLayoutChanges}
                            onButtonTap={this._handleCanvasButtonTap}
                            BackgroundImage={settings.BackgroundImage}
                            MobileBackgroundImage={settings.MobileBackgroundImage}
                            GridLayout={settings.GridLayout}
                            CanvasBackgroundColor={settings.CanvasBackgroundColor}
                            Padding={settings.Padding}
                            ElementSettings={{
                                zDepth: settings.ZDepth,
                                BackgroundColor: settings.WidgetBackgroundColor,
                                NoBackground: settings.WidgetNoBackground,
                                BorderRadius: settings.BorderRadius,
                                Outline: settings.Outline
                            }}
                            HeightPixels={Layout.heightPixels}
                            DisableScrollingY={
                                maxDashboardWidth > widthListThreshold &&
                                dashboard.Settings.LayoutPolicy == PageLayoutPolicy.FillHeight &&
                                !inMobileMode &&
                                !inEditMode
                            }
                        />
                    </div>
                </div>
            );
        } else
            return (
                <div
                    key={"paper-empty"}
                    className="noselect"
                    style={{
                        width: 400, height: 200,
                        textAlign: 'center',
                        margin: '20px auto',
                        fontSize: 'x-large',
                        fontWeight: 700,
                        backgroundColor: 'white',
                        borderRadius: 10,
                        boxShadow: getZDepthShadow(2)
                    }}>
                    <p>
                        <div
                            style={{
                                width: 120, height: 120,
                                padding: 30,
                                margin: "0 auto",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                if (!this.inMobileMode())
                                    this.toggleEditMode(true);
                            }}
                        >
                            <i style={{ fontSize: "60px" }} className="fas fa-plane-departure"></i>
                        </div>
                    </p>
                    <p>
                        {"You can add some widgets."}
                    </p>
                </div >);
    }
    // ------------------------------------------------------------------------------------------------------
    renderCanvasReport(dashboardWidth: number, dashboardHeight: number) {
        // Check if dashboard property is defined
        const inEditMode = this.state.inEditMode && this.state.previewMode == PreviewModes.Disabled;
        return <PageReporting
            ReportPage={this.state.loadedDashboard}
            Pages={this.state.dashboards}
            handleCanvasButtonTap={this._handleCanvasButtonTap}
            height={dashboardHeight}
            width={dashboardWidth}
            inEditMode={inEditMode}
            onPageChange={_.noop}
            settingsKey={this.state.settingsKey}
        />
    }
    // ------------------------------------------------------------------------------------------------------
    /*  Layout  */
    renderCanvas = (dashboardWidth: number, dashboardHeight: number) => {
        if (this.state.isLoading)
            return <div
                style={{
                    width: dashboardWidth,
                    height: dashboardHeight,
                    backgroundColor: this.Theme.boardGrid.backgroundColor
                }}
            ></div>;

        let dashboard = this.state.loadedDashboard;

        // draw a placeholder
        if (dashboard == null)
            return (
                <div
                    key='paper-empty'
                    className='noselect'
                    style={{
                        boxShadow: getZDepthShadow(2),
                        width: 400, height: 200,
                        textAlign: 'center',
                        margin: '20px auto',
                        fontSize: 'x-large',
                        fontWeight: 700,
                        borderRadius: 10
                    }}>
                    <p>

                        <div style={{
                            width: 120, height: 120,
                            padding: 30,
                            margin: "0 auto"
                        }}>
                            <i style={{ fontSize: "60px" }} className="fas fa-hand-pointer"></i>
                        </div>
                    </p>
                    <p>
                        {"Please select a page."}
                    </p>
                </div >
            );

        if (dashboard.Settings.Type == DashboardType.Page)
            return this.renderCanvasPage(dashboardWidth, dashboardHeight);

        if (dashboard.Settings.Type == DashboardType.Reporting)
            return this.renderCanvasReport(dashboardWidth, dashboardHeight);

        return null;
    }
    // ------------------------------------------------------------------------------------------------------
    public static getLayout(dashboard: DashboardDescriptor,
        inEditMode: boolean,
        inMobileMode: boolean,
        dashboardHeight: number,
        runtimeWidgetsVisibility: { [key: string]: boolean },
        forceFillPolicy: boolean = false
    ): {
        Layout: CanvasLayout,
        heightPixels: number | undefined
    } {
        const Layout: CanvasLayout = {
            LayoutDesc: {},
            ItemDesc: {}
        };

        // translate the widgets to canvas items
        _.forOwn(dashboard.Widgets, (value, wKey) => {
            // merge the override settings
            let description = _.merge({}, value.Description, value.OverrideDescription);

            // get canvas layout descriptor
            Layout.LayoutDesc[wKey] = DashboardManager._GetCanvasItemLayoutDesc(dashboard.ViewWidgets.Layout, description, dashboard.Settings.NumberOfVerticalGridSpaces);

            // The second condition is for the enabled preview in mobile.
            if (inMobileMode) {
                // Hidden in mobile view?
                if (value.Description.MobileIsHidden)
                    return;
            }
            else {
                // Hidden in desktop view?
                if (value.Description.IsHidden) {
                    if (inEditMode) {
                        // get canvas item descriptor
                        Layout.ItemDesc[wKey] = DashboardManager._GetCanvasItemDesc(wKey, value, description);
                        Layout.ItemDesc[wKey].IsHidden = true;
                    }
                    return;
                }
            }

            let widgetKey = (value as IWidgetThingsClass<any>).NodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + (value as IWidgetThingsClass<any>).ThingKey;
            if (runtimeWidgetsVisibility[widgetKey] == false) return;

            // get canvas item descriptor
            Layout.ItemDesc[wKey] = DashboardManager._GetCanvasItemDesc(wKey, value, description);
        });

        // Check if we have items
        if (_.keys(Layout.ItemDesc).length > 0) {

            // Calculate the height pixels
            let heightPixels: number = undefined;
            if (forceFillPolicy) {
                let hMax = 0;
                _.forOwn(Layout.LayoutDesc, (c) => {
                    if (_.has(dashboard.Widgets, c.Key) &&
                        _.has(Layout.ItemDesc, c.Key)) {
                        let bottom = c.Y + c.H;
                        if (hMax < bottom)
                            hMax = bottom;
                    }
                });
                heightPixels = (dashboardHeight - 5) / hMax;
            } else if (!inEditMode && dashboard.Settings.LayoutPolicy != PageLayoutPolicy.FillWidth) {
                let hMax = 0;
                _.forOwn(Layout.LayoutDesc, (c) => {
                    if (_.has(dashboard.Widgets, c.Key) &&
                        _.has(Layout.ItemDesc, c.Key)) {
                        let bottom = c.Y + c.H;
                        if (hMax < bottom)
                            hMax = bottom;
                    }
                });
                let newHeightPixels = (dashboardHeight - 5) / hMax;
                if ((dashboard.Settings.LayoutPolicy == PageLayoutPolicy.FillHeight) ||
                    (dashboard.Settings.LayoutPolicy == PageLayoutPolicy.AvoidScrollbar && newHeightPixels < heightPixels))
                    heightPixels = newHeightPixels;
            }

            return { Layout, heightPixels: heightPixels };
        }

        return null;
    }
    // ------------------------------------------------------------------------------------------------------
    private renderPopupPage() {
        const { popupPages } = this.state;
        if (!isDefined(popupPages) ||
            popupPages.length == 0)
            return null;

        return _(popupPages)
            .sortBy(x => x.info.zIndex)
            .map((p, i) => {
                const settings = BoardTheming.getDashboardConfiguration(this.props.nodeKey, p.dashboard.Settings);
                const { title, isStatic, width, height } = p.info;
                const showTitle = !IsNullOrEmpty(title);

                const closeCB = () => {
                    if (!p.info.onCloseConfirm)
                        return this.closeDialogDashboard([p.info.zIndex]);

                    this.showGenericDialog({
                        actions: [
                            <RSButton
                                appearance="ghost"
                                size="md"
                                style={{
                                    margin: '0 4px',
                                    border: 0,
                                    color: "#CC4580"
                                }}
                                onClick={() => { this.closeGenericDialog() }}
                            >{'CANCEL'}</RSButton>,
                            <RSButton
                                appearance="ghost"
                                size="md"
                                style={{
                                    margin: '0 4px',
                                    border: 0,
                                    color: "#03a4bb"
                                }}
                                onClick={() => {
                                    this.closeGenericDialog()
                                    this.closeDialogDashboard([p.info.zIndex])
                                }}
                            >{'CLOSE'}</RSButton>
                        ],
                        title: "Confirm close",
                        body: <div style={{
                            width: 400,
                            height: 90,
                            padding: 24,
                            color: "initial"
                        }}>
                            Are you sure you want to close this dialog?<br />
                            Any pending changes might not be saved.
                        </div>
                    })

                }

                return <ExDialog
                    key={`dialogs-${i}`}
                    Enable={true}
                    style={{ backgroundColor: settings.CanvasBackgroundColor }}
                    Conf={{
                        contentStaticSize: { w: width, h: height },
                        handleClose: isStatic ? null : closeCB,
                        title: title,
                        actionsFunc: !showTitle ? null :
                            (isStatic !== true ?
                                (theme) => [
                                    <IconButton
                                        tooltip="Close"
                                        iconClassName="fas fa-times"
                                        iconStyle={{
                                            fontWeight: "bold",
                                            fontSize: "14px",
                                            lineHeight: theme.dialog.height + "px",
                                            color: theme.dialog.toolbarFontColor
                                        }}
                                        onClick={closeCB} />
                                ]
                                : null),
                        bodyFunc: (dialogWidth, dialogHeight) => {

                            // (backwards compatibility issues) render toolbar if old port event is used
                            let dialogTitleHeight = showTitle || isStatic ? 0 : 15;
                            dialogHeight = dialogHeight - dialogTitleHeight;

                            const Layout = DashboardManager.getLayout(
                                p.dashboard,
                                false,
                                this.inMobileMode(),
                                dialogHeight,
                                this.widgetsVisibility);

                            // define the WidthListThreshold
                            let WidthListThreshold = (this.props.board.Settings.WidthListThreshold != undefined) ? this.props.board.Settings.WidthListThreshold : 800;
                            if (settings.WidthListThreshold != null)
                                WidthListThreshold = settings.WidthListThreshold;

                            if (Layout != null)
                                return <div
                                    id={settings.PageId}
                                    style={{
                                        maxWidth: settings.MaxPageWidth,
                                        height: dialogHeight + dialogTitleHeight,
                                        margin: "auto"
                                    }}>

                                    {dialogTitleHeight > 0 ?
                                        <div key={'pagedialog-title'}
                                            style={{
                                                width: "100%",
                                                height: dialogTitleHeight,
                                                overflow: 'hidden',
                                                backgroundColor: this.Theme.dialog.toolbarBackgroundColor,
                                                padding: "0px 5px",
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                alignItems: "center",
                                                alignContent: "center"
                                            }}>
                                            <IconButton
                                                tooltip="Close"
                                                iconClassName="fas fa-times"
                                                iconStyle={{
                                                    fontWeight: "bold",
                                                    fontSize: "14px",
                                                    lineHeight: dialogTitleHeight + "px",
                                                    color: this.Theme.dialog.toolbarFontColor
                                                }}
                                                onClick={closeCB} />
                                        </div>
                                        : null}

                                    <Canvas key={this.canvasKey + "-dialog"} width={dialogWidth} height={dialogHeight}
                                        WidthListThreshold={WidthListThreshold}
                                        SizeW={settings.NumberOfVerticalGridSpaces}
                                        Layout={Layout.Layout}
                                        CompactLayout={settings.VerticalCompact}
                                        InEditMode={false}
                                        Margin={settings.Margin}
                                        BackgroundImage={settings.BackgroundImage}
                                        MobileBackgroundImage={settings.MobileBackgroundImage}
                                        GridLayout={settings.GridLayout}
                                        CanvasBackgroundColor={settings.CanvasBackgroundColor}
                                        Padding={settings.Padding}
                                        ElementSettings={{
                                            zDepth: settings.ZDepth,
                                            BackgroundColor: settings.WidgetBackgroundColor,
                                            NoBackground: settings.WidgetNoBackground,
                                            BorderRadius: settings.BorderRadius,
                                            Outline: settings.Outline
                                        }}
                                        HeightPixels={Layout.heightPixels} />

                                </div>;
                        }
                    }} />
            })
            .value();
    }
    // ------------------------------------------------------------------------------------------------------
    private toolbarIconElement: SettingsElementItemsMgmtDialog<any> = {
        type: SettingsType.ItemsMgmtDialog,
        dialogStaticSize: 0.95,
        icon: null,
        label: "Toolbar Buttons",
        dataPath: "ToolbarIcons",
        itemsInfo: ToolbarIconInfo_GetSettingList(),
        createNewItem: () => {
            return {
                icon: "",
                tooltip: "Icon tooltip",
                onIconClickDropdown: false,
                iconType: eIconType.fontAwesome,
                id: "Icon " + getRandomSmallKey(),
                nestedItems: []
            } as ToolbarIconInfo;
        },
    };
    private renderToolbarIconsEdit() {
        if (!this.state.editingToolbarIcons)
            return;

        // mount a hidden div :P
        return <div
            style={{ display: 'none' }}
        >
            <SettingsListItemsDialog
                key="ToolbarIcons"
                Key="ToolbarIcons"
                isListItem={false}
                startOpen={true}
                groupLevel={0}
                showTooltip={_.noop}
                hideTooltip={_.noop}
                DataPath="ToolbarIcons"
                Data={this.props.board.Settings}
                element={this.toolbarIconElement}
                onSaveData={this.onToolbarIconsSave}
                onCancel={this.onToolbarIconsCancel}
            />
        </div>
    }
    // ------------------------------------------------------------------------------------------------------
    private onToolbarIconsSave = (
        ItemsMgmt: ToolbarIconInfo[],
        ItemsToolbarToggleState: { [key: string]: boolean }) => {
        if (isDefined(this.props.onToolbarIconsChange))
            RunSafe(() => this.props.onToolbarIconsChange(ItemsMgmt));

        // unmount settings list
        this.onToolbarIconsCancel();
    }
    // ------------------------------------------------------------------------------------------------------
    private onToolbarIconsCancel = () => this.setState({ editingToolbarIcons: false });
    // ------------------------------------------------------------------------------------------------------
    render() {
        // TODO: Create the magic numbers to style
        let listWidth = this.Theme.pageList.width;
        const { loadedRightDrawerPage,
            loadedLeftDrawerPage,
            loadedBottomDrawerPage,
            isListEnabled,
            isListModal,
            isListOpen } = this.state;

        const secondaryDrawerWidth = isDefined(loadedLeftDrawerPage) && !this.state.inEditMode && loadedLeftDrawerPage.position == "left" && loadedLeftDrawerPage.open ? this._getDrawerPageSize(loadedLeftDrawerPage) : 0;
        const inMobileMode = this.inMobileMode();
        // If we have open list when we are in mobile mode the list is the main viewer
        if (inMobileMode && this.state.isListOpen) {
            listWidth = this.props.width;
        }

        const settings = BoardTheming.getDashboardConfiguration(this.props.nodeKey, this.state.loadedDashboard?.Settings);
        const boardToolbarElements = this.props?.getToolbarElements();

        const iconStyle: React.CSSProperties = {
            fontSize: this.Theme.boardToolbar.fontSize
        };

        return <React.Fragment>
            <ExDialog
                style={{ paddingTop: 0 }}
                key="dashboard-manager-dialog"
                Enable={isDefined(this.state.dialogInfo)}
                Conf={this.state.dialogInfo}
            />

            {this.renderToolbarIconsEdit()}

            <AdvanceLayout
                width={this.props.width}
                height={this.props.height}
                MainBody={this.renderCanvas}
                style={{
                    backgroundColor: settings.CanvasBackgroundColor
                }}
                MainToolbar={{
                    Size: this.Theme.boardToolbar.height,
                    style: {
                        backgroundColor: this.Theme.boardToolbar.style.backgroundColor,
                        color: this.Theme.boardToolbar.style.fontColor,
                        fontSize: this.Theme.boardToolbar.fontSize,
                        boxShadow: getZDepthShadow(this.Theme.boardToolbar.style.shadowLevel),
                        overflow: 'auto hidden'
                    },
                    CenterElements: [
                        ...boardToolbarElements.CenterElements,
                        ...ToolbarIconInfo_ButtonRender(
                            this.state.toolbarIcons?.center,
                            this.state.hiddenToolbarIcons,
                            this.state.selectedToolbarIcons,
                            iconStyle,
                            this.toolbarIconsOnClick,
                            null,
                            this.buttonPressedFromAdvancedToolbarIcon,
                            this.elementChangedFromAdvancedToolbarIcon)
                    ],
                    LeftElements:
                        (isDefined(this.state.loadedDashboard)) ?
                            [
                                // Handle also the case with unpinned list.
                                inMobileMode && isListEnabled
                                    ? <i
                                        id='icons'
                                        className={!isListModal
                                            ? 'fas fa-chevron-right'
                                            : 'fas fa-chevron-circle-' + (isListOpen ? 'up' : 'down')}
                                        onClick={() => this.setState({ isListOpen: isListOpen ? false : true })} />
                                    : null,

                                (!this.state.inEditMode && isDefined(loadedLeftDrawerPage) && secondaryDrawerWidth == 0) ? <i id="icons" className={loadedLeftDrawerPage.fontIcon} onClick={() => this.openDrawerPage(loadedLeftDrawerPage, true)} /> : null,
                                this.props.board.Settings.PageShowTitle ?
                                    <div
                                        key={"Dashboard-Info-Toolbar"}
                                        style={{
                                            fontSize: 20,
                                            color: this.Theme.boardToolbar.style.fontColor,
                                            paddingLeft: '16px',
                                            maxWidth: Math.round(this.props.width / 2)
                                        }}
                                    >
                                        {this.state.localizationMap['page_name_' + this.state.loadedDashboard.Key]}
                                    </div> : null,
                                !isStringEmpty(this.state.localizationMap['page_desc_' + this.state.loadedDashboard.Key]) ?
                                    <ToolbarIconButton
                                        key="page_desc"
                                        tooltip={this.state.localizationMap['page_desc_' + this.state.loadedDashboard.Key]}
                                        id="icons"
                                        className="fas fa-info"
                                        style={{ color: this.Theme.boardToolbar.style.fontColor, cursor: "default" }} />
                                    : null,
                                ...boardToolbarElements.LeftElements,
                                ...ToolbarIconInfo_ButtonRender(
                                    this.state.toolbarIcons?.left,
                                    this.state.hiddenToolbarIcons,
                                    this.state.selectedToolbarIcons,
                                    iconStyle,
                                    this.toolbarIconsOnClick,
                                    null,
                                    this.buttonPressedFromAdvancedToolbarIcon,
                                    this.elementChangedFromAdvancedToolbarIcon),
                            ]
                            : undefined,
                    RightElements: [
                        ...ToolbarIconInfo_ButtonRender(
                            this.state.toolbarIcons?.right,
                            this.state.hiddenToolbarIcons,
                            this.state.selectedToolbarIcons,
                            iconStyle,
                            this.toolbarIconsOnClick,
                            null,
                            this.buttonPressedFromAdvancedToolbarIcon,
                            this.elementChangedFromAdvancedToolbarIcon),
                        ...this.getMainToolbarItems(),
                        ...boardToolbarElements.RightElements
                    ]
                }}

                LeftDrawer={isListEnabled ? {
                    InitialOpen: isListOpen,
                    InsideBody: isListModal,
                    IsModal: isListModal,
                    IsHoverable: false,
                    FillSpace: false,
                    UseDropdownIcon: true,
                    IsStatic: inMobileMode,
                    Render: (w, h) => this.renderDashboardList(w, h),
                    Size: listWidth,
                    Toolbar: {
                        Size: this.Theme.boardToolbar.height,
                        style: {
                            backgroundColor: this.Theme.pageList.toolbarBackgroundColor,
                            color: this.Theme.pageList.toolbarFontColor,
                            boxShadow: getZDepthShadow(this.Theme.boardToolbar.style.shadowLevel)
                        },
                        LeftElements: this.getListToolbar(),
                        RightElements: [
                            <ToolbarIconButton
                                tooltip={(isListModal) ? "Pin list" : "Unpin list"}
                                id="icons"
                                className="fas fa-thumbtack"
                                style={{ color: (!isListModal) ? grey600 : "white" }}
                                onClick={() => this.setState({ isListModal: !isListModal })} />
                        ]
                    },
                    style: {
                        backgroundColor: this.Theme.pageList.style.backgroundColor,
                        overflow: "hidden",
                        boxShadow: getZDepthShadow(this.Theme.pageList.style.shadowLevel)
                    }
                } : null}

                SecondaryLeftDrawer={secondaryDrawerWidth > 0 ? {
                    InitialOpen: loadedLeftDrawerPage.open,
                    IsModal: loadedLeftDrawerPage.isModal,
                    InsideBody: loadedLeftDrawerPage.insideBody,
                    IsHoverable: false,
                    FillSpace: false,
                    UseDropdownIcon: true,
                    IsStatic: true,
                    style: { borderRadius: (loadedLeftDrawerPage.insideBody) ? "0px 10px 10px 0px" : undefined },
                    Render: (w, h) => this.renderDrawerPage(loadedLeftDrawerPage, w, h),
                    Size: secondaryDrawerWidth,
                    // handle open/close externally iff isStatic is disabled
                    Toolbar: {
                        RightElements: (loadedLeftDrawerPage.isStatic === false) ? [
                            <div style={{ paddingRight: 10 }}>{loadedLeftDrawerPage.title}</div>,
                            (loadedLeftDrawerPage.isStatic === false) ? <i id="icons" className={"fas fa-chevron-left"} onClick={() => { this.closeDrawerPage(loadedLeftDrawerPage.position, false); }} /> : null,
                        ] : []

                    }
                } : null}

                RightDrawer={(isDefined(loadedRightDrawerPage) && !this.state.inEditMode && loadedRightDrawerPage.position == "right") ? {
                    InitialOpen: loadedRightDrawerPage.open,
                    IsModal: loadedRightDrawerPage.isModal,
                    InsideBody: loadedRightDrawerPage.insideBody,
                    IsHoverable: false,
                    FillSpace: false,
                    UseDropdownIcon: true,
                    IsStatic: true,
                    style: { borderRadius: (loadedRightDrawerPage.insideBody) ? "10px 0 0 10px" : undefined },
                    Render: (w, h) => this.renderDrawerPage(loadedRightDrawerPage, w, h),
                    // handle absolute pixels value
                    Size: this._getDrawerPageSize(loadedRightDrawerPage),
                    // handle open/close externally iff isStatic is disabled
                    Toolbar: {
                        LeftElements: [
                            (loadedRightDrawerPage.isStatic === false) ? <i id="icons" className={"fas fa-chevron-right"} onClick={() => { this.closeDrawerPage(loadedRightDrawerPage.position, false); }} /> : null,
                            <div style={{ paddingLeft: 10 }}>{loadedRightDrawerPage.title}</div>
                        ],
                    }
                } : null}

                BottomDrawer={(isDefined(loadedBottomDrawerPage) && !this.state.inEditMode && loadedBottomDrawerPage.position == "bottom") ? {
                    InitialOpen: loadedBottomDrawerPage.open,
                    IsModal: loadedBottomDrawerPage.isModal,
                    IsHoverable: false,
                    FillSpace: false,
                    UseDropdownIcon: true,
                    IsStatic: true,
                    Render: (w, h) => this.renderDrawerPage(loadedBottomDrawerPage, w, h),
                    Size: this._getDrawerPageSize(loadedBottomDrawerPage),
                    // handle open/close externally iff isStatic is disabled
                    Toolbar: {
                        LeftElements: [<div style={{ paddingLeft: 10 }}>{loadedBottomDrawerPage.title}</div>],
                        RightElements: loadedBottomDrawerPage.isStatic === false ?
                            [
                                <i id="icons" className={"fas fa-chevron-down"} onClick={() => { this.closeDrawerPage(loadedBottomDrawerPage.position, false); }} />
                            ]
                            : [],
                    }
                } : null}

                BottomToolbar={
                    isDefined(loadedBottomDrawerPage) &&
                        loadedBottomDrawerPage.isStatic === false &&
                        loadedBottomDrawerPage.position == "bottom" &&
                        !loadedBottomDrawerPage.open ?
                        {
                            LeftElements: [],
                            RightElements: [
                                <i id="icons" className={loadedBottomDrawerPage.fontIcon} onClick={() => { this.openDrawerPage(loadedBottomDrawerPage, true); }} />
                            ]
                        } : null
                }
            />

            {this.renderPopupPage()}

            {
                (this.state.isConfigurationOpen) ?
                    <DashboardConfigurationView
                        inEditMode={this.state.inEditMode}
                        isDrawer={false/*(this.state.configurationDashboardKey == "") ? true : false*/}
                        isOpen={this.state.isConfigurationOpen}
                        drawerWidth={this.Theme.pageList.width}
                        debug={false}
                        configurationKey={(this.state.configPageKey != "") ? this.state.configPageKey : ((this.state.loadedDashboard != null) ? this.state.loadedDashboard.Key : "")}
                        configuration={
                            (this.state.configPageKey != "" && this.state.dashboards[this.state.configPageKey] != undefined) ?
                                this.state.dashboards[this.state.configPageKey].Settings :
                                ((this.state.loadedDashboard != null) ?
                                    this.state.loadedDashboard.Settings :
                                    DashboardHelper.getDefaultConfiguration())}
                        onConfigurationChange={this.onDashboardConfigurationChanges}
                        onConfigurationClose={this.settingsDashboardClose}
                        otherPages={_.map(this.props.dashboards, d => d.Settings)}
                    />
                    : null
            }

            <Snackbar
                open={this.state.snackBarOpen}
                message={this.state.snackBarMessage}
                autoHideDuration={3000}
                onRequestClose={() => { this.setState({ snackBarMessage: "", snackBarOpen: false }) }}
            />

            <Infotip
                maxWidth={350}
                open={this.state.tooltip.open}
                content={this.state.tooltip.content}
                xPos={this.state.tooltip.xPos}
                yPos={this.state.tooltip.yPos} />
        </React.Fragment>
    }
    // ------------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: DashboardManagerProps, prevState: DashboardManagerState) {
        if (inDebug() && this.props.debug)
            console.log("DashboardManager:componentDidUpdate() prevProps:%O", prevProps);

        // Update running macros
        if (!_.isEqual(this.props.board.Macros, prevProps.board.Macros)) {
            let boardMacroEngine = BoardMacroEngine.GetInstance(this.props.nodeKey);
            if (isDefined(boardMacroEngine)) {
                boardMacroEngine.setMacros(this.props.board.Macros as MacroInfoEx[]);
            }
        }

        // If the app saved the work then mark that we are not have any unsaved work
        const hasUnsavedWork = _.isEqual(this.props.dashboards, prevProps.dashboards) && this.checkForUnsavedWork(this.props.dashboards, this.state.loadedDashboard);

        // If the saved have done
        if (!hasUnsavedWork &&
            prevProps.isSaving &&
            !this.props.isSaving
        ) {
            // if we have pending key
            if (isDefined(this.state.pendingKeyToLoad))
                //just load it
                this.loadDashboard(this.props.dashboards[this.state.pendingKeyToLoad]);
            else
                // else update pages list
                this.setState({
                    dashboardStructure: this.createDashboardStructure(this.state, this.props)
                });
            // load the dashboard if is different from the loaded one (key based)
        } else if (_.hasIn(this.props.dashboards, this.props.selectedDashboardKey) &&
            !(isDefined(this.state.loadedDashboard) && this.state.loadedDashboard.Key == this.props.selectedDashboardKey) &&
            this._mounted) {
            this.loadDashboard(this.props.dashboards[this.props.selectedDashboardKey]);
        } else if (this.props.isLoading) {
            const { loadedDashboard } = this.state;
            this.setState({
                loadedDashboard: null,
                inEditMode: false,
                previewMode: PreviewModes.Disabled,
                hasUnsavedWork: false,
                isConfigurationOpen: false,
                configPageKey: "",
                dashboards: _.cloneDeep(this.props.dashboards),
                dashboardStructure: this.createDashboardStructure(this.state, this.props)
            }, () => {
                this._updatePulseMechanism(null, loadedDashboard);
            });
        } else if (!_.isEqual(this.state.dashboards, this.props.dashboards)) {
            this.setState((state: DashboardManagerState) => {
                if (!_.isEqual(state.dashboards, this.props.dashboards)) {
                    state.dashboards = _.cloneDeep(this.props.dashboards);
                }
                if (isStringEmpty(this.props.selectedDashboardKey)) {
                    this._updatePulseMechanism(null, state.loadedDashboard);
                    state.loadedDashboard = null;
                }
                state.localizationMap = this._fillLocalizationDictionary(state.dashboards, true);
                state.dashboardStructure = this.createDashboardStructure(state, this.props);
                state.hasUnsavedWork = hasUnsavedWork;
                return state;
            });
        }

        // Update board macro engine with edit mode status
        if (this.props.supportEditing != prevProps.supportEditing) {
            let boardMacroView = BoardMacroEngine.GetInstance(this.props.nodeKey);
            if (isDefined(boardMacroView))
                boardMacroView.setInEditMode(this.props.supportEditing);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    public CheckForUpdatedData = (key: string, newWidgetData: any): void => {
        this.setState((prevState: DashboardManagerState) => {
            prevState.loadedDashboard.Widgets[key].Description.Data = _.cloneDeep(newWidgetData);
            prevState.loadedDashboard.ViewWidgets.WidgetsDesc[key].Data = _.cloneDeep(newWidgetData);
            prevState.hasUnsavedWork = this.checkForUnsavedWork(prevState.dashboards, prevState.loadedDashboard);
            this._revision += 1;
            return prevState;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    public UpdateWidgetDescription(newDescription: IWidgetDescription): void {
        DebugEx.TraceLog("UpdateWidgetDescription", newDescription);
        if (this.state.loadedDashboard != null) {
            this.setState((s: DashboardManagerState) => {
                // update `Widgets` and `ViewWidgets` description
                s.loadedDashboard.Widgets[newDescription.Key].Description = _.cloneDeep(newDescription);
                s.loadedDashboard.ViewWidgets.WidgetsDesc[newDescription.Key] = _.cloneDeep(newDescription);
                s.hasUnsavedWork = this.checkForUnsavedWork(s.dashboards, s.loadedDashboard);

                // Update localization
                s.loadedDashboard.Widgets[newDescription.Key].UpdateLocalization();
                return s;
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    // Utils dashboard management
    // ------------------------------------------------------------------------------------------------------
    public static _GetCanvasItemDesc(key: string, widgetDesc: IWidget, description: IWidgetDescription): CanvasItemDesc {
        return {
            Title: (widgetDesc as IWidgetThingsClass<any>).GetLocalizedWidgetTitle() || description.WidgetTitle,
            ToolbarBackgroundColor: description.TitleStyle.BackgroundColor,
            ToolbarFontColor: description.TitleStyle.FontColor,
            ToolbarFontAutoSize: description.TitleStyle.FontAutoSize,
            ToolbarFontSize: description.TitleStyle.FontSize,
            ToolbarTooltip: description.TitleStyle.Tooltip,
            ToolbarIcon: description.TitleStyle.Icon,
            Key: key,
            Alignment: description.TitleStyle.Alignment,
            TitleHeight: description.TitleStyle.TitleHeight,
            BackgroundColor: description.WidgetBackgroundColor,
            ShowExtraMenu: (description.ShowExtraMenu) ? true : false,
            Render: (k, w, h) => widgetDesc.RenderBase(k, w, h),
            RenderToolbar: (k, toolbarHeight) => widgetDesc.RenderBaseToolbar(k, toolbarHeight),
            ToolbarDynamicTooltip: () => widgetDesc.ToolbarDynamicTooltip(),
            IsHidden: false,
            Outline: description.Outline
        };
    }
    // ------------------------------------------------------------------------------------------------------
    private _GetNewDashboardItemLayoutDesc(layout: { [key: string]: DashboardItemLayoutDesc }, widgetDesc: IWidgetDescription, numberOfVerticalGridSpaces: number): DashboardItemLayoutDesc {
        // find the last Y position of the list
        let lastY = 0;
        _.forOwn(layout, l => {
            if (l.Y + l.H > lastY) {
                lastY = l.Y + l.H;
            }
        });

        // Select middle widget size
        let widgetH = widgetDesc.MinH > 6 ? widgetDesc.MinH : 6;
        let widgetW = Math.floor((2 * widgetDesc.MinW + numberOfVerticalGridSpaces) / 3);

        return {
            H: widgetH,
            W: widgetW,

            MinH: widgetDesc.MinH,
            MinW: widgetDesc.MinW,
            IsDraggable: true,
            IsResizable: true,

            X: 5,
            Y: lastY,
            Key: widgetDesc.Key
        };
    }
    // ------------------------------------------------------------------------------------------------------
    private static _GetCanvasItemLayoutDesc(
        layout: { [key: string]: DashboardItemLayoutDesc },
        widgetDesc: IWidgetDescription,
        numberOfVerticalGridSpaces: number): CanvasItemLayoutDesc {
        const layoutItem = layout[widgetDesc.Key];

        if (!isDefined(layoutItem)) {
            // find the last Y position of the list
            let lastY = 0;
            _.forOwn(layout, l => {
                if (l.Y + l.H > lastY) {
                    lastY = l.Y + l.H;
                }
            });

            // Select middle widget size
            let widgetH = widgetDesc.MinH > 6 ? widgetDesc.MinH : 6;
            let widgetW = Math.floor((2 * widgetDesc.MinW + numberOfVerticalGridSpaces) / 3);

            return {
                H: widgetH,
                W: widgetW,

                MinH: widgetDesc.MinH,
                MinW: widgetDesc.MinW,

                IsDraggable: true,
                IsResizable: true,
                IsStatic: false,

                X: 5,
                Y: lastY,

                Key: widgetDesc.Key,

                DefiesGrid: widgetDesc.DefiesGrid,
                WidgetNoBackground: widgetDesc.WidgetNoBackground,
                MobileOrder: widgetDesc.MobileOrder
            };
        }
        return {
            H: layoutItem.H,
            W: layoutItem.W,

            MinH: layoutItem.MinH,
            MinW: layoutItem.MinW,

            IsDraggable: layoutItem.IsDraggable,
            IsResizable: layoutItem.IsResizable,
            IsStatic: layoutItem.IsStatic,

            X: layoutItem.X,
            Y: layoutItem.Y,

            Key: widgetDesc.Key,

            DefiesGrid: widgetDesc.DefiesGrid,
            WidgetNoBackground: widgetDesc.WidgetNoBackground,
            MobileOrder: widgetDesc.MobileOrder
        };
    }
    // ------------------------------------------------------------------------------------------------------
    public static GetDocumentation() {
        return Documentation;
    }
    // ------------------------------------------------------------------------------------------------------
    private _getDrawerPageSize(drawerPage: DrawerPage): number {
        const maxSize = drawerPage.position == "bottom" ? screenHeight() : screenWidth();
        // handle absolute & normalized pixels value
        const size = drawerPage.size > 1 ?
            _.min([drawerPage.size, maxSize]) :
            drawerPage.size * maxSize;
        // round result
        return Math.floor(size);
    }
    // ------------------------------------------------------------------------------------------------------
    // Report Page functions
    // ------------------------------------------------------------------------------------------------------
    addNewReportPage = () => {
        if (isDefined(this.state.loadedDashboard) &&
            this.state.loadedDashboard.Settings.Type == DashboardType.Reporting) {
            this.setState((s: DashboardManagerState, props) => {
                if (!isDefined(s.loadedDashboard.ViewWidgets.ReportPages))
                    s.loadedDashboard.ViewWidgets.ReportPages = {}
                let reportPages = s.loadedDashboard.ViewWidgets.ReportPages;

                let newKey = getRandomKey();
                let index = _.keys(reportPages).length;
                let order = ((index > 0) ? _.max(_.map(reportPages, x => x.Order)) : 0) + 1;
                s.loadedDashboard.ViewWidgets.ReportPages[newKey] = {
                    Name: "Page-" + index,
                    Order: order,
                    Layout: {},
                    WidgetsDesc: {},
                    PageProxyGUID: null
                };

                s.hasUnsavedWork = this.checkForUnsavedWork(
                    props.dashboards,
                    s.loadedDashboard
                );
                return s;
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    addNewProxyReportPage = () => {
        if (isDefined(this.state.loadedDashboard) &&
            this.state.loadedDashboard.Settings.Type == DashboardType.Reporting) {

            this.setState({
                dialogInfo: {
                    title: "Select Page",
                    contentStaticSize: { w: 0.5, h: 0.9 },
                    handleClose: () => this.setState((s: DashboardManagerState, props) => {
                        s.dialogInfo = null;
                        s.hasUnsavedWork = this.checkForUnsavedWork(
                            props.dashboards,
                            s.loadedDashboard
                        );
                        return s;
                    }),
                    actions: [
                        <IconButton tooltip="Close" iconClassName="fas fa-times-circle" iconStyle={{ color: "white" }}
                            onClick={() => this.setState((s: DashboardManagerState, props) => {
                                s.dialogInfo = null;
                                s.hasUnsavedWork = this.checkForUnsavedWork(
                                    props.dashboards,
                                    s.loadedDashboard
                                );
                                return s;
                            })}
                        />
                    ],
                    bodyFunc: (w, h) => {
                        let structure = TreeStructureLite.helperPathSplitter(
                            _.filter(_.values(this.state.dashboards), d => d.Settings.Type != DashboardType.Reporting),
                            "Settings.Path",
                            (item, path, id) => {
                                let isSelected = isDefined(_.find(this.state.loadedDashboard.ViewWidgets.ReportPages, r => r.PageProxyGUID == item.Settings.PageId));

                                return {
                                    _id: id,
                                    key: item.Key,
                                    path: path,
                                    primaryText: item.Settings.Name,
                                    secondaryText: item.Settings.Desc,
                                    parentId: 0,
                                    type: FolderItemType.Dashboard,
                                    children: [],
                                    style: {},
                                    rightIcon: <IconButton
                                        iconStyle={{ height: 20, color: "white" }}
                                        onClick={() => {
                                            this.setState((s: DashboardManagerState, props) => {
                                                if (!isDefined(s.loadedDashboard.ViewWidgets.ReportPages))
                                                    s.loadedDashboard.ViewWidgets.ReportPages = {};
                                                let reportPages = s.loadedDashboard.ViewWidgets.ReportPages;

                                                // If is allready exist remove it
                                                if (isSelected) {
                                                    let key = _.findKey(reportPages, p => p.PageProxyGUID == item.Key);
                                                    delete reportPages[key];
                                                } else {
                                                    let newKey = getRandomKey();
                                                    let index = _.keys(reportPages).length;
                                                    let order = ((index > 0) ? _.max(_.map(reportPages, x => x.Order)) : 0) + 1;
                                                    s.loadedDashboard.ViewWidgets.ReportPages[newKey] = {
                                                        Name: item.Settings.Name,
                                                        Order: order,
                                                        Layout: {},
                                                        WidgetsDesc: {},
                                                        PageProxyGUID: item.Settings.PageId
                                                    };
                                                }

                                                s.hasUnsavedWork = this.checkForUnsavedWork(
                                                    props.dashboards,
                                                    s.loadedDashboard
                                                );
                                                return s;
                                            });
                                        }}
                                        iconClassName={isSelected ? "fas fa-check-square" : "far fa-square"}
                                    />
                                }
                            },
                            (filename, path, id) => {
                                return {
                                    _id: id, children: [], path: path, key: path,
                                    type: FolderItemType.Folder, primaryText: filename, parentId: 0,
                                    leftFontIcon: this._foldersState[id] ? ["fa-folder-open"] : ["fa-folder"], open: this._foldersState[id],
                                };
                            });
                        return <div style={{ width: w, height: h, backgroundColor: "rgb(38, 40, 34)", color: "white" }}>
                            <TreeStructureLite
                                style={{ backgroundColor: "rgb(38, 40, 34)" }}
                                secondaryTextLimitLine={2}
                                FolderStructure={structure}
                                onNestedListToggle={(id: number) => {
                                    this._foldersState[id] = !this._foldersState[id];
                                    this.forceUpdate();
                                }}
                            />
                        </div>
                    }
                }
            });

        }
    }
    // ------------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------------
